/**
 * SPA shell: login (BFF cookie) → AI accounts / PATs / MCP setup.
 * Invite accept flow: /invite/accept?token=… stores token in
 * sessionStorage, preview pre-login, accept after session.
 * Cognito tokens never touch localStorage.
 */

import * as api from "./api.js";
import { mcpSetupConfig } from "./config.js";
import {
  clear,
  copyText,
  el,
  enhancePasswordFields,
  formatDate,
  withShowPassword,
} from "./dom.js";
import {
  ApiError,
  DEFAULT_FEATURE_FLAGS,
  isAdminRole,
  isLoginChallenge,
  scopesOf,
  type Account,
  type AccountDefaults,
  type AdminFlag,
  type AdminInvite,
  type AdminUser,
  type AuditEvent,
  type BillingSummary,
  type FeatureFlags,
  type FlagStatus,
  type InvitePreview,
  type InviteStatus,
  type McpClient,
  type MintedPat,
  type PatMeta,
  type PatScope,
  type PromoRow,
  type UsageModelRow,
  type UsageSummary,
  type UsageWindow,
  type UserProfile,
  type UserRole,
} from "./types.js";
import type { JoinRequestRow } from "./api.js";

/** sessionStorage key for pre-login invite token (never localStorage). */
export const INVITE_TOKEN_STORAGE_KEY = "inbox_invite_token";

type ChallengeState =
  | { kind: "NEW_PASSWORD_REQUIRED"; session: string; email: string }
  | { kind: "SOFTWARE_TOKEN_MFA"; session: string; email: string }
  | null;

/** Unauthenticated password-reset UI (SPA BFF → Cognito ForgotPassword). */
type ForgotPasswordState =
  | { step: "request" }
  | { step: "confirm"; email: string }
  | null;

type InviteFlowState = {
  /** Raw inbox_inv_… held only in memory + sessionStorage. */
  token: string | null;
  preview: InvitePreview | null;
  /** Accept succeeded this session. */
  accepted: boolean;
  /** Preview/accept in progress. */
  loading: boolean;
};

type AppState = {
  user: UserProfile | null;
  challenge: ChallengeState;
  /** Forgot-password panel when signed out (not a Cognito login challenge). */
  forgotPassword: ForgotPasswordState;
  accounts: Account[];
  /** Account open in the edit panel (click row). */
  selectedAccountId: string | null;
  tokens: PatMeta[];
  /** MCP OAuth + PAT connections (Active clients list). */
  mcpClients: McpClient[];
  minted: MintedPat | null;
  features: FeatureFlags;
  /** Own audit events (GET /api/v1/audit). */
  auditEvents: AuditEvent[];
  auditNextCursor: string | null;
  /** Show "Add AI account" form under Connected AIs. */
  addAccountOpen: boolean;
  /** Admin anomaly flags (GET /api/v1/admin/flags) — owner/admin only. */
  adminFlags: AdminFlag[];
  adminFlagsNextCursor: string | null;
  adminFlagsStatusFilter: FlagStatus | "all";
  /** Admin invites (GET /api/v1/admin/invites) — requires invites.productized. */
  adminInvites: AdminInvite[];
  adminInviteStatusFilter: InviteStatus | "all";
  /** One-time invite URL from create/resend (shown until dismissed/copied). */
  lastInviteLink: { email: string; inviteUrl: string } | null;
  /** Admin users (GET /api/v1/admin/users). */
  adminUsers: AdminUser[];
  /** Public join-the-test waitlist (admin). */
  joinRequests: JoinRequestRow[];
  joinRequestStatusFilter: "pending" | "invited" | "dismissed" | "all";
  /** Commercial billing summary (null until loaded). */
  billing: BillingSummary | null;
  usage: UsageSummary | null;
  /** Selected token period on Plan & usage (one breakdown at a time). */
  usagePeriod: "day" | "week" | "month" | "d90";
  /** Profile menu open (top-right account menu). */
  profileMenuOpen: boolean;
  /** Admin promo list. */
  adminPromos: PromoRow[];
  loading: boolean;
  busy: string | null;
  error: string | null;
  notice: string | null;
  invite: InviteFlowState;
};

const state: AppState = {
  user: null,
  challenge: null,
  forgotPassword: null,
  accounts: [],
  selectedAccountId: null,
  tokens: [],
  mcpClients: [],
  minted: null,
  features: { ...DEFAULT_FEATURE_FLAGS },
  auditEvents: [],
  auditNextCursor: null,
  addAccountOpen: false,
  adminFlags: [],
  adminFlagsNextCursor: null,
  adminFlagsStatusFilter: "open",
  adminInvites: [],
  adminInviteStatusFilter: "pending",
  lastInviteLink: null,
  adminUsers: [],
  joinRequests: [],
  joinRequestStatusFilter: "pending",
  billing: null,
  usage: null,
  usagePeriod: "day",
  profileMenuOpen: false,
  adminPromos: [],
  loading: true,
  busy: null,
  error: null,
  notice: null,
  invite: {
    token: null,
    preview: null,
    accepted: false,
    loading: false,
  },
};

const AUDIT_PAGE_SIZE = 25;
const FLAGS_PAGE_SIZE = 25;

let root: HTMLElement;

export function mount(container: HTMLElement): void {
  // Browser back/forward for /profile /billing /admin
  window.addEventListener("popstate", () => {
    state.profileMenuOpen = false;
    render();
    if (state.user && isAdminRole(state.user.role) && isAdminRoute()) {
      void refreshAdminData().then(() => render());
    }
  });

  root = container;
  void boot();
}

/** Read OAuth callback / deep-link query on /accounts or /. */
function consumeAccountsQuery(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const connected = url.searchParams.get("connected");
    const err = url.searchParams.get("error");
    const errDesc = url.searchParams.get("error_description");
    if (connected === "1") {
      state.notice = "Account connected successfully.";
    } else if (err) {
      state.error =
        errDesc && errDesc.trim()
          ? `Connect failed: ${errDesc}`
          : `Connect failed (${err})`;
    }
    if (connected || err) {
      url.searchParams.delete("connected");
      url.searchParams.delete("error");
      url.searchParams.delete("error_description");
      const clean =
        url.pathname +
        (url.searchParams.toString() ? `?${url.searchParams}` : "") +
        url.hash;
      window.history.replaceState({}, "", clean || "/");
    }
  } catch {
    // ignore malformed URL
  }
}

function readSessionInviteToken(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const t = sessionStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
    return t && t.startsWith("inbox_inv_") ? t : null;
  } catch {
    return null;
  }
}

function writeSessionInviteToken(token: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
  } catch {
    // private mode / quota — keep in-memory only
  }
}

function clearSessionInviteToken(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Capture invite token from /invite/accept?token=… into sessionStorage,
 * strip token from the URL (avoid history leakage), keep path for UI.
 */
function consumeInviteQuery(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const path = url.pathname.replace(/\/+$/, "") || "/";
    const isAcceptPath =
      path === "/invite/accept" || path.endsWith("/invite/accept");
    const qToken = url.searchParams.get("token");
    if (qToken && qToken.startsWith("inbox_inv_")) {
      writeSessionInviteToken(qToken);
      state.invite.token = qToken;
      url.searchParams.delete("token");
      const cleanPath = isAcceptPath ? "/invite/accept" : path;
      const clean =
        cleanPath +
        (url.searchParams.toString() ? `?${url.searchParams}` : "") +
        url.hash;
      window.history.replaceState({}, "", clean);
    } else if (isAcceptPath || readSessionInviteToken()) {
      state.invite.token = readSessionInviteToken();
    }
  } catch {
    // ignore
  }
}

function currentPath(): string {
  if (typeof window === "undefined") return "/";
  try {
    return new URL(window.location.href).pathname.replace(/\/+$/, "") || "/";
  } catch {
    return "/";
  }
}

/** Admin console lives at /admin (CloudFront SPA fallback → index.html). */
function isAdminRoute(): boolean {
  return currentPath() === "/admin";
}

/** Plans & billing page. */
function isBillingRoute(): boolean {
  return currentPath() === "/billing";
}

/** Edit profile page. */
function isProfileRoute(): boolean {
  return currentPath() === "/profile";
}

/** Activity log page. */
function isActivityRoute(): boolean {
  return currentPath() === "/activity";
}

function navigateApp(path: string): void {
  state.profileMenuOpen = false;
  try {
    const next = path.startsWith("/") ? path : `/${path}`;
    if (currentPath() !== next) {
      window.history.pushState({}, "", next);
    }
  } catch {
    // ignore
  }
  render();
}

function isInviteAcceptRoute(): boolean {
  if (typeof window === "undefined") return !!state.invite.token;
  try {
    const path = currentPath();
    return (
      path === "/invite/accept" ||
      path.endsWith("/invite/accept") ||
      !!state.invite.token
    );
  } catch {
    return !!state.invite.token;
  }
}

async function loadInvitePreview(): Promise<void> {
  const token = state.invite.token;
  if (!token) return;
  state.invite.loading = true;
  try {
    const preview = await api.previewInvite(token);
    state.invite.preview = preview;
  } catch (err) {
    state.invite.preview = null;
    setError(inviteErrorMessage(err));
  } finally {
    state.invite.loading = false;
  }
}

/**
 * After session login, bind JWT/session sub via POST accept.
 * Idempotent same-sub; mismatch → 401; expired → 410; other sub → 409.
 */
async function tryAcceptPendingInvite(): Promise<boolean> {
  const token = state.invite.token ?? readSessionInviteToken();
  if (!token || !state.user) return false;
  state.invite.token = token;
  setBusy("invite-accept");
  try {
    const res = await api.acceptInvite(token);
    if (res.status === "pending_login") {
      // Session missing on server — keep token for retry.
      return false;
    }
    state.invite.accepted = true;
    clearSessionInviteToken();
    state.invite.token = null;
    setNotice(`Invite accepted for ${res.email}. Welcome!`);
    setError(null);
    // Leave /invite/accept path for a clean dashboard URL.
    if (typeof window !== "undefined") {
      try {
        window.history.replaceState({}, "", "/");
      } catch {
        // ignore
      }
    }
    return true;
  } catch (err) {
    setError(inviteErrorMessage(err));
    return false;
  } finally {
    setBusy(null);
  }
}

function inviteErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return "Signed-in email does not match this invite. Sign out and use the invited address.";
    }
    if (err.status === 410) {
      return err.message || "This invite has expired or was revoked.";
    }
    if (err.status === 409) {
      return err.message || "This invite was already accepted by another account.";
    }
    if (err.status === 404) {
      return "Invite link is invalid or has already been used.";
    }
    return err.message || err.code;
  }
  return errMessage(err);
}

async function boot(): Promise<void> {
  state.loading = true;
  state.error = null;
  consumeAccountsQuery();
  consumeInviteQuery();
  render();

  // Pre-login preview (safe metadata) when we have a token.
  if (state.invite.token) {
    await loadInvitePreview();
  }

  try {
    const me = await api.getMe();
    state.user = me;
    if (state.invite.token) {
      await tryAcceptPendingInvite();
    }
    await refreshDashboard();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      state.user = null;
    } else {
      // Network / server down — show login; surface soft message
      state.user = null;
      if (!(err instanceof ApiError && err.status === 401)) {
        state.notice = errMessage(err);
      }
    }
  } finally {
    state.loading = false;
    render();
  }
}

async function refreshDashboard(): Promise<void> {
  const [accounts, tokens, mcpClients, features, auditPage, billing, usage] =
    await Promise.all([
      api.listAccounts(),
      api.listTokens(),
      api.listMcpClients().catch(() => [] as McpClient[]),
      api.tryGetFeatures(),
      api.listAudit({ limit: AUDIT_PAGE_SIZE }),
      api.tryGetBilling(),
      api.tryGetUsage(),
    ]);
  state.accounts = accounts;
  state.tokens = tokens;
  state.mcpClients = mcpClients;
  state.features = features;
  state.auditEvents = auditPage.events;
  state.auditNextCursor = auditPage.nextCursor ?? null;
  state.billing = billing;
  state.usage = usage;

  // Admin lists only load on /admin (keeps dashboard light).
  if (state.user && isAdminRole(state.user.role) && isAdminRoute()) {
    await refreshAdminData();
  } else if (!isAdminRoute()) {
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    state.adminInvites = [];
    state.adminUsers = [];
    state.joinRequests = [];
    state.lastInviteLink = null;
  }
}

/** Load admin-only lists (invites, users, join requests, flags). Soft-fails per section. */
async function refreshAdminData(): Promise<void> {
  try {
    state.adminPromos = await api.listPromos();
  } catch {
    state.adminPromos = [];
  }
  // Users always available to admin (not gated by invites.productized).
  try {
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    state.adminUsers = [];
    if (err instanceof ApiError && err.status !== 403) {
      setNotice(`Admin users unavailable: ${errMessage(err)}`);
    }
  }

  try {
    const status =
      state.joinRequestStatusFilter === "all"
        ? undefined
        : state.joinRequestStatusFilter;
    state.joinRequests = await api.listJoinRequests(
      status ? { status } : undefined,
    );
  } catch (err) {
    state.joinRequests = [];
    if (err instanceof ApiError && err.status !== 403) {
      setNotice(`Join requests unavailable: ${errMessage(err)}`);
    }
  }

  // Invites require invites.productized.
  if (state.features["invites.productized"]) {
    try {
      state.adminInvites = await api.listAdminInvites();
    } catch (err) {
      state.adminInvites = [];
      if (err instanceof ApiError && err.status !== 403) {
        setNotice(`Admin invites unavailable: ${errMessage(err)}`);
      }
    }
  } else {
    state.adminInvites = [];
  }

  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const flagsPage = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      ...(status ? { status } : {}),
    });
    state.adminFlags = flagsPage.flags;
    state.adminFlagsNextCursor = flagsPage.nextCursor ?? null;
  } catch (err) {
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    if (err instanceof ApiError && err.status !== 403) {
      setNotice(`Admin flags unavailable: ${errMessage(err)}`);
    }
  }
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message || err.code;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

function setBusy(key: string | null): void {
  state.busy = key;
  render();
}

function setError(msg: string | null): void {
  state.error = msg;
}

function setNotice(msg: string | null): void {
  state.notice = msg;
}

// ── Render ────────────────────────────────────────────────────────────────

function render(): void {
  clear(root);
  if (state.loading) {
    root.append(
      el(
        "div",
        { className: "boot" },
        el("div", { className: "spinner" }),
        el("div", null, "Checking session…"),
      ),
    );
    return;
  }
  if (!state.user) {
    // Unauthenticated /admin → same login; path preserved so post-login lands on admin.
    root.append(renderLogin());
  } else if (isAdminRoute()) {
    if (isAdminRole(state.user.role)) {
      root.append(renderAdminShell());
    } else {
      // Non-admins do not get a console — send them home.
      try {
        window.history.replaceState({}, "", "/");
      } catch {
        // ignore
      }
      root.append(renderShell());
    }
  } else {
    root.append(renderShell());
  }
  // Safety net: any password input missing a toggle gets one.
  enhancePasswordFields(root);
  // Close profile menu on outside click / Escape.
  if (state.profileMenuOpen && state.user) {
    wireProfileMenuDismiss();
  }
}

let profileMenuDismissWired = false;
function wireProfileMenuDismiss(): void {
  if (profileMenuDismissWired) return;
  profileMenuDismissWired = true;
  document.addEventListener(
    "click",
    (ev) => {
      if (!state.profileMenuOpen) return;
      const t = ev.target;
      if (!(t instanceof Node)) return;
      const menu = document.querySelector(".profile-menu");
      if (menu && menu.contains(t)) return;
      state.profileMenuOpen = false;
      render();
    },
    true,
  );
  document.addEventListener("keydown", (ev) => {
    if (!state.profileMenuOpen) return;
    if (ev.key === "Escape") {
      state.profileMenuOpen = false;
      render();
    }
  });
}

/** Favicon-matched brand mark used in top bars and login. */
function brandMarkEl(): HTMLElement {
  return el("img", {
    className: "brand-mark",
    src: "/favicon.svg",
    alt: "",
    width: "28",
    height: "28",
    decoding: "async",
  });
}

function renderLogin(): HTMLElement {
  const wrap = el("div", { className: "login-wrap" });
  const card = el("div", { className: "login-card" });

  card.append(
    el(
      "div",
      { className: "brand" },
      brandMarkEl(),
      el("span", null, "MCP to AI"),
    ),
  );

  if (state.challenge?.kind === "NEW_PASSWORD_REQUIRED") {
    card.append(
      el("h1", null, "Set a new password"),
      el(
        "p",
        { className: "lede" },
        "Your temporary password must be replaced before you can continue.",
      ),
    );
    if (isInviteAcceptRoute() && state.invite.preview) {
      card.append(inviteBanner());
    }
    if (state.error) card.append(alertBox("error", state.error));
    card.append(newPasswordForm(state.challenge.email, state.challenge.session));
  } else if (state.challenge?.kind === "SOFTWARE_TOKEN_MFA") {
    card.append(
      el("h1", null, "Two-factor authentication"),
      el("p", { className: "lede" }, "Enter the code from your authenticator app."),
    );
    if (state.error) card.append(alertBox("error", state.error));
    card.append(totpForm(state.challenge.email, state.challenge.session));
  } else if (state.forgotPassword?.step === "request") {
    card.append(
      el("h1", null, "Forgot password"),
      el(
        "p",
        { className: "lede" },
        "Enter your account email. If it exists, we will send a verification code.",
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(forgotPasswordRequestForm());
  } else if (state.forgotPassword?.step === "confirm") {
    card.append(
      el("h1", null, "Reset password"),
      el(
        "p",
        { className: "lede" },
        `Enter the code we emailed to ${state.forgotPassword.email} and choose a new password.`,
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(forgotPasswordConfirmForm(state.forgotPassword.email));
  } else if (isInviteAcceptRoute()) {
    card.append(
      el("h1", null, "Accept your invite"),
      el(
        "p",
        { className: "lede" },
        "Sign in with the invited email. After login we bind your account to this invite.",
      ),
    );
    card.append(inviteBanner());
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    if (state.invite.loading) {
      card.append(el("p", { className: "hint" }, "Checking invite…"));
    } else if (state.invite.token) {
      card.append(loginForm({ prefillEmail: state.invite.preview?.email }));
    } else {
      card.append(
        alertBox("error", "Missing invite token. Open the full link from your email."),
      );
    }
  } else {
    card.append(
      el("h1", null, "Sign in"),
      el(
        "p",
        { className: "lede" },
        "Sign in with your mcpto.ai account. New here? Create an account or use an invite link.",
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(loginForm());
    card.append(
      el(
        "p",
        { className: "hint join-signin-link" },
        el("a", { href: "/signup" }, "Create an account"),
        " · ",
        el("a", { href: "/join" }, "Join waitlist"),
        " · have an invite? open the link from your email.",
      ),
    );
  }

  wrap.append(card);
  return wrap;
}

function inviteBanner(): HTMLElement {
  const box = el("div", {
    className: "invite-banner",
    role: "status",
  });
  const preview = state.invite.preview;
  if (preview) {
    box.append(
      el(
        "div",
        { className: "invite-banner-title" },
        "Invite for ",
        el("strong", null, preview.email),
      ),
      el(
        "p",
        { className: "hint" },
        [
          preview.role ? `Role: ${preview.role}` : null,
          preview.expiresAt
            ? `Expires ${formatDate(preview.expiresAt)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || "Sign in to accept.",
      ),
    );
  } else if (state.invite.token && !state.invite.loading && !state.error) {
    box.append(el("p", { className: "hint" }, "Invite token ready — sign in to accept."));
  }
  return box;
}

function loginForm(opts?: { prefillEmail?: string }): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const email = el("input", {
    type: "email",
    name: "email",
    autocomplete: "username",
    required: true,
    placeholder: "you@example.com",
    value: opts?.prefillEmail ?? "",
  }) as HTMLInputElement;
  const password = el("input", {
    type: "password",
    name: "password",
    autocomplete: "current-password",
    required: true,
    placeholder: "Password",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Email", email),
    el("label", { className: "field" }, "Password", withShowPassword(password)),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "login" ? true : undefined,
        },
        state.busy === "login"
          ? "Signing in…"
          : isInviteAcceptRoute()
            ? "Sign in & accept invite"
            : "Sign in",
      ),
      el(
        "button",
        {
          type: "button",
          className: "btn-link",
        },
        "Forgot password?",
      ),
    ),
  );

  form.querySelector(".btn-link")?.addEventListener("click", () => {
    state.forgotPassword = { step: "request" };
    state.challenge = null;
    setError(null);
    setNotice(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onLogin(email.value.trim(), password.value);
  });
  return form;
}

function forgotPasswordRequestForm(): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const email = el("input", {
    type: "email",
    name: "email",
    autocomplete: "username",
    required: true,
    placeholder: "you@example.com",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Email", email),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "forgot" ? true : undefined,
        },
        state.busy === "forgot" ? "Sending…" : "Send reset code",
      ),
      el("button", { type: "button", className: "btn-ghost" }, "Back to sign in"),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.forgotPassword = null;
    setError(null);
    setNotice(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onForgotPasswordRequest(email.value.trim());
  });
  return form;
}

function forgotPasswordConfirmForm(accountEmail: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const code = el("input", {
    type: "text",
    name: "code",
    inputmode: "numeric",
    autocomplete: "one-time-code",
    required: true,
    placeholder: "Verification code",
  }) as HTMLInputElement;
  const pw = el("input", {
    type: "password",
    name: "newPassword",
    autocomplete: "new-password",
    required: true,
    placeholder: "New password",
    minlength: "12",
  }) as HTMLInputElement;
  const confirm = el("input", {
    type: "password",
    name: "confirm",
    autocomplete: "new-password",
    required: true,
    placeholder: "Confirm new password",
  }) as HTMLInputElement;

  form.append(
    el(
      "label",
      { className: "field" },
      "Account",
      el("input", {
        type: "email",
        value: accountEmail,
        disabled: true,
        readonly: true,
      }),
    ),
    el("label", { className: "field" }, "Verification code", code),
    el("label", { className: "field" }, "New password", withShowPassword(pw)),
    el("label", { className: "field" }, "Confirm", withShowPassword(confirm)),
    el(
      "p",
      { className: "hint" },
      "Use at least 12 characters with upper, lower, number, and symbol (pool policy).",
    ),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "forgot" ? true : undefined,
        },
        state.busy === "forgot" ? "Updating…" : "Update password",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost", "data-action": "resend" },
        "Resend code",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost", "data-action": "back" },
        "Back to sign in",
      ),
    ),
  );

  form
    .querySelector('[data-action="back"]')
    ?.addEventListener("click", () => {
      state.forgotPassword = null;
      setError(null);
      setNotice(null);
      render();
    });
  form
    .querySelector('[data-action="resend"]')
    ?.addEventListener("click", () => {
      void onForgotPasswordRequest(accountEmail);
    });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (pw.value !== confirm.value) {
      setError("Passwords do not match");
      render();
      return;
    }
    void onForgotPasswordConfirm(accountEmail, code.value.trim(), pw.value);
  });
  return form;
}

function newPasswordForm(email: string, session: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const pw = el("input", {
    type: "password",
    name: "newPassword",
    autocomplete: "new-password",
    required: true,
    placeholder: "New password",
    minlength: "8",
  }) as HTMLInputElement;
  const confirm = el("input", {
    type: "password",
    name: "confirm",
    autocomplete: "new-password",
    required: true,
    placeholder: "Confirm new password",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Account", el("input", {
      type: "email",
      value: email,
      disabled: true,
      readonly: true,
    })),
    el("label", { className: "field" }, "New password", withShowPassword(pw)),
    el("label", { className: "field" }, "Confirm", withShowPassword(confirm)),
    el("p", { className: "hint" }, "Use at least 8 characters. Avoid reusing passwords."),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "login" ? true : undefined,
        },
        state.busy === "login" ? "Saving…" : "Set password & sign in",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost" },
        "Back",
      ),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.challenge = null;
    setError(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (pw.value !== confirm.value) {
      setError("Passwords do not match");
      render();
      return;
    }
    void onNewPassword(email, session, pw.value);
  });
  return form;
}

function totpForm(email: string, session: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const code = el("input", {
    type: "text",
    name: "totpCode",
    inputmode: "numeric",
    autocomplete: "one-time-code",
    required: true,
    placeholder: "6-digit code",
    maxlength: "8",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Authenticator code", code),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "login" ? true : undefined,
        },
        state.busy === "login" ? "Verifying…" : "Verify",
      ),
      el("button", { type: "button", className: "btn-ghost" }, "Back"),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.challenge = null;
    setError(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onTotp(email, session, code.value.trim());
  });
  return form;
}

/** Shared top bar: brand left, profile menu right. */
function renderTopbar(opts: { adminPage?: boolean } = {}): HTMLElement {
  const top = el("header", { className: "topbar" });
  const brand = el(
    "a",
    {
      href: "/",
      className: "brand brand-link",
      title: "MCP to AI",
    },
    brandMarkEl(),
    el("span", null, "MCP to AI"),
  );
  brand.addEventListener("click", (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigateApp("/");
  });
  if (opts.adminPage) {
    brand.append(el("span", { className: "brand-admin-badge" }, "Admin"));
  }

  const meta = el("div", { className: "user-meta" });
  meta.append(profileMenuButton());

  top.append(brand, meta);
  return top;
}

function profileInitials(user: UserProfile): string {
  const name = (user.displayName ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (
        (parts[0]![0] ?? "") + (parts[parts.length - 1]![0] ?? "")
      ).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = user.email || "?";
  return email.slice(0, 2).toUpperCase();
}

/** Top-right profile control + dropdown. */
function profileMenuButton(): HTMLElement {
  const user = state.user!;
  const wrap = el("div", { className: "profile-menu" });

  const trigger = el(
    "button",
    {
      type: "button",
      className: "profile-menu-trigger",
      "aria-haspopup": "menu",
      "aria-expanded": state.profileMenuOpen ? "true" : "false",
      title: user.email,
    },
    el("span", { className: "profile-avatar" }, profileInitials(user)),
    el("span", { className: "profile-trigger-label" }, "Profile"),
  ) as HTMLButtonElement;
  trigger.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    state.profileMenuOpen = !state.profileMenuOpen;
    render();
  });
  wrap.append(trigger);

  if (!state.profileMenuOpen) return wrap;

  const menu = el("div", {
    className: "profile-menu-dropdown",
    role: "menu",
  });
  menu.append(
    el(
      "div",
      { className: "profile-menu-header" },
      el(
        "div",
        { className: "profile-menu-name" },
        user.displayName?.trim() || user.email,
      ),
      el("div", { className: "profile-menu-email" }, user.email),
    ),
  );

  const item = (
    label: string,
    onClick: () => void,
    opts?: { danger?: boolean },
  ): HTMLButtonElement => {
    const btn = el(
      "button",
      {
        type: "button",
        className: opts?.danger
          ? "profile-menu-item profile-menu-item-danger"
          : "profile-menu-item",
        role: "menuitem",
      },
      label,
    ) as HTMLButtonElement;
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onClick();
    });
    return btn;
  };

  menu.append(
    item("Edit profile", () => navigateApp("/profile")),
    item("Plans & billing", () => navigateApp("/billing")),
    item("Activity log", () => navigateApp("/activity")),
  );

  if (state.features["docs.portal"]) {
    menu.append(
      item("Setup docs", () => {
        state.profileMenuOpen = false;
        try {
          window.location.href = "/docs";
        } catch {
          // ignore
        }
      }),
    );
  }

  if (isAdminRole(user.role)) {
    menu.append(
      item("Admin", () => {
        state.profileMenuOpen = false;
        try {
          window.history.pushState({}, "", "/admin");
        } catch {
          // ignore
        }
        void (async () => {
          render();
          try {
            await refreshAdminData();
          } catch {
            // soft
          }
          render();
        })();
      }),
    );
  }

  menu.append(el("div", { className: "profile-menu-sep" }));
  menu.append(
    item(
      state.busy === "logout" ? "Signing out…" : "Sign out",
      () => {
        void onLogout();
      },
      { danger: true },
    ),
  );

  wrap.append(menu);
  return wrap;
}

function renderShell(): HTMLElement {
  const shell = el("div", { className: "app-shell" });
  shell.append(renderTopbar());

  if (state.error) shell.append(alertBox("error", state.error));
  if (state.notice) shell.append(alertBox("ok", state.notice));

  // Pending invite while already signed in (e.g. opened link after login).
  if (state.invite.token && !state.invite.accepted) {
    shell.append(pendingInvitePanel());
  }

  if (isProfileRoute()) {
    shell.append(profilePanel());
    return shell;
  }

  if (isBillingRoute()) {
    if (state.features["billing.ui"]) {
      shell.append(billingUsagePanel());
    } else {
      shell.append(
        el(
          "section",
          { className: "panel" },
          el("h2", null, "Plans & billing"),
          el(
            "p",
            { className: "empty" },
            "Billing UI is not enabled for this environment.",
          ),
        ),
      );
    }
    return shell;
  }

  if (isActivityRoute()) {
    shell.append(auditPanel());
    return shell;
  }

  // Home dashboard
  shell.append(accountsPanel());
  shell.append(mcpAccessPanel());

  return shell;
}

/** Profile page: display name + change password. */
function profilePanel(): HTMLElement {
  const wrap = el("div", { className: "profile-page" });
  wrap.append(profileIdentityPanel());
  wrap.append(profilePasswordPanel());
  return wrap;
}

function profileIdentityPanel(): HTMLElement {
  const user = state.user!;
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Edit profile"),
        el(
          "p",
          { className: "sub" },
          "Update how your name appears in the app. Email is your sign-in identity.",
        ),
      ),
    ),
  );

  const form = el("form", { className: "profile-form" }) as HTMLFormElement;
  const nameInput = el("input", {
    type: "text",
    className: "input",
    name: "displayName",
    maxlength: "80",
    autocomplete: "name",
    placeholder: "Display name",
    value: user.displayName ?? "",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Display name", nameInput),
    el(
      "label",
      { className: "field" },
      "Email",
      el("input", {
        type: "email",
        className: "input",
        value: user.email,
        disabled: "true",
        readonly: "true",
      }),
      el("span", { className: "hint" }, "Email cannot be changed here."),
    ),
    el(
      "label",
      { className: "field" },
      "Role",
      el("input", {
        type: "text",
        className: "input",
        value: user.role,
        disabled: "true",
        readonly: "true",
      }),
    ),
  );

  const actions = el("div", { className: "row-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary btn-sm",
      disabled: state.busy === "profile-save" ? true : undefined,
    },
    state.busy === "profile-save" ? "Saving…" : "Save profile",
  ) as HTMLButtonElement;
  const backBtn = el(
    "button",
    { type: "button", className: "btn-ghost btn-sm" },
    "Back to dashboard",
  ) as HTMLButtonElement;
  backBtn.addEventListener("click", () => navigateApp("/"));
  actions.append(saveBtn, backBtn);
  form.append(actions);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void (async () => {
      setBusy("profile-save");
      setError(null);
      setNotice(null);
      render();
      try {
        const updated = await api.updateProfile({
          displayName: nameInput.value,
        });
        state.user = updated;
        setNotice("Profile saved.");
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(null);
        render();
      }
    })();
  });

  panel.append(form);
  return panel;
}

function profilePasswordPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Change password"),
        el(
          "p",
          { className: "sub" },
          "Use your current password, then choose a new one (min 12 characters, with upper, lower, number, and symbol).",
        ),
      ),
    ),
  );

  const form = el("form", { className: "profile-form" }) as HTMLFormElement;
  const currentIn = el("input", {
    type: "password",
    className: "input",
    name: "currentPassword",
    autocomplete: "current-password",
    placeholder: "Current password",
  }) as HTMLInputElement;
  const newIn = el("input", {
    type: "password",
    className: "input",
    name: "newPassword",
    autocomplete: "new-password",
    placeholder: "New password",
    minlength: "12",
  }) as HTMLInputElement;
  const confirmIn = el("input", {
    type: "password",
    className: "input",
    name: "confirmPassword",
    autocomplete: "new-password",
    placeholder: "Confirm new password",
    minlength: "12",
  }) as HTMLInputElement;

  form.append(
    el(
      "label",
      { className: "field" },
      "Current password",
      withShowPassword(currentIn),
    ),
    el(
      "label",
      { className: "field" },
      "New password",
      withShowPassword(newIn),
    ),
    el(
      "label",
      { className: "field" },
      "Confirm new password",
      withShowPassword(confirmIn),
    ),
  );

  const actions = el("div", { className: "row-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary btn-sm",
      disabled: state.busy === "password-change" ? true : undefined,
    },
    state.busy === "password-change" ? "Updating…" : "Update password",
  ) as HTMLButtonElement;
  actions.append(saveBtn);
  form.append(actions);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const previousPassword = currentIn.value;
    const newPassword = newIn.value;
    const confirm = confirmIn.value;
    if (!previousPassword || !newPassword) {
      setError("Current and new password are required.");
      render();
      return;
    }
    if (newPassword !== confirm) {
      setError("New password and confirmation do not match.");
      render();
      return;
    }
    if (newPassword.length < 12) {
      setError("New password must be at least 12 characters.");
      render();
      return;
    }
    void (async () => {
      setBusy("password-change");
      setError(null);
      setNotice(null);
      render();
      try {
        const res = await api.changePassword({
          previousPassword,
          newPassword,
        });
        setNotice(res.message ?? "Password updated.");
        currentIn.value = "";
        newIn.value = "";
        confirmIn.value = "";
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(null);
        render();
      }
    })();
  });

  panel.append(form);
  return panel;
}

function fmtTok(n: number | undefined): string {
  return (n ?? 0).toLocaleString();
}

type UsagePeriodId = "day" | "week" | "month" | "d90";

const USAGE_PERIOD_OPTIONS: Array<{
  id: UsagePeriodId;
  short: string;
}> = [
  { id: "day", short: "Today" },
  { id: "week", short: "7 days" },
  { id: "month", short: "Month" },
  { id: "d90", short: "90 days" },
];

function resolveUsageWindow(u: UsageSummary, id: UsagePeriodId): UsageWindow {
  const win = u.windows?.[id];
  if (win != null) {
    return {
      ...win,
      models: Array.isArray(win.models) ? win.models : [],
    };
  }
  // Fallback when API is older than windows support.
  return {
    id: "month",
    label: `This month (${u.period})`,
    from: u.period,
    to: u.today,
    inputTokens: u.inputTokens ?? 0,
    outputTokens: u.outputTokens ?? 0,
    totalTokens: u.totalTokens ?? 0,
    requestCount: u.requestCount,
    models: Array.isArray(u.topModels) ? u.topModels : [],
  };
}

/**
 * Compact tokens UI: period toggles + by-model detail only.
 * Each model line: in · out · total · requests.
 */
function tokenWindowsSection(u: UsageSummary): HTMLElement {
  const box = el("div", {
    className: "list-item usage-token-windows",
  });

  const available: UsagePeriodId[] = u.windows
    ? USAGE_PERIOD_OPTIONS.map((o) => o.id).filter((id) => !!u.windows?.[id])
    : ["month"];
  let selected: UsagePeriodId = state.usagePeriod;
  if (!available.includes(selected)) {
    selected = available[0] ?? "day";
    state.usagePeriod = selected;
  }

  const w = resolveUsageWindow(u, selected);
  let models: UsageModelRow[] = Array.isArray(w.models) ? [...w.models] : [];
  if (
    models.length === 0 &&
    Array.isArray(u.topModels) &&
    u.topModels.length > 0
  ) {
    models = u.topModels.map((m) => ({ ...m }));
  }

  const toggles = el("div", {
    className: "usage-period-toggles",
    role: "tablist",
    "aria-label": "Token period",
  });
  for (const opt of USAGE_PERIOD_OPTIONS) {
    if (!available.includes(opt.id)) continue;
    const active = opt.id === selected;
    const btn = el(
      "button",
      {
        type: "button",
        className: active
          ? "btn-sm btn-primary usage-period-btn"
          : "btn-ghost btn-sm usage-period-btn",
        role: "tab",
        "aria-selected": active ? "true" : "false",
      },
      opt.short,
    ) as HTMLButtonElement;
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (state.usagePeriod === opt.id) return;
      state.usagePeriod = opt.id;
      render();
    });
    toggles.append(btn);
  }
  box.append(toggles);

  if (models.length > 0) {
    const list = el("ul", {
      className: "usage-breakdown",
      "data-period": selected,
    });
    for (const m of models) {
      const inTok = m.inputTokens ?? 0;
      const outTok = m.outputTokens ?? 0;
      const total =
        m.tokens ?? (inTok || outTok ? inTok + outTok : 0);
      const reqs = m.count ?? 0;
      const li = el("li");
      const name = m.name ?? "";
      const isMediaModel =
        /imagine|image|video|dall-e|gpt-image|flux|imagen/i.test(name) ||
        total === 0;
      // Image/video providers price per image or per second — not tokens.
      let detail: string;
      if (total > 0) {
        detail = ` — in ${fmtTok(inTok)} · out ${fmtTok(outTok)} · total ${fmtTok(total)} · ${fmtTok(reqs)} req`;
      } else if (isMediaModel) {
        detail = ` — ${fmtTok(reqs)} call${reqs === 1 ? "" : "s"} · not token-priced (per image / per sec)`;
      } else {
        detail = ` — ${fmtTok(reqs)} call${reqs === 1 ? "" : "s"} · no token usage reported`;
      }
      li.append(el("strong", null, m.name), document.createTextNode(detail));
      list.append(li);
    }
    box.append(list);
  } else {
    box.append(
      el(
        "div",
        { className: "list-item-meta usage-breakdown-empty" },
        "No model usage for this period yet.",
      ),
    );
  }

  return box;
}

/** Plan, promo redeem, bandwidth usage. Stripe checkout when keys are live. */
function billingUsagePanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  const b = state.billing;
  const u = state.usage;

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Plan & usage"),
        el(
          "p",
          { className: "sub" },
          "Platform subscription (BYOK). $5/mo or $50/yr · 10 GB fair-use bandwidth included.",
        ),
      ),
    ),
  );

  if (!b) {
    panel.append(
      el("p", { className: "empty" }, "Billing summary unavailable."),
    );
    return panel;
  }

  const statusLine = el("div", { className: "list-item" });
  statusLine.append(
    el(
      "div",
      { className: "list-item-main" },
      el(
        "div",
        { className: "list-item-title" },
        `Status: ${b.subscriptionStatus}`,
        b.plan ? ` · ${b.plan}` : "",
      ),
      el(
        "div",
        { className: "list-item-meta" },
        b.access.roleBypass
          ? "Owner/admin bypass"
          : b.access.softAllow
            ? "Dogfood access (billing not enforced yet)"
            : b.access.allowed
              ? "Active entitlement"
              : `Blocked: ${b.access.reason}`,
        b.periodEnd ? ` · period ends ${formatDate(b.periodEnd)}` : "",
      ),
    ),
  );
  panel.append(statusLine);

  if (u) {
    const mcpLabel = u.format.mcp ?? "—";
    const mediaLabel = u.format.media ?? "—";
    const images = u.imageCount ?? 0;
    const videoSecs = u.videoSeconds ?? 0;
    const usageLine = el("div", { className: "list-item" });
    usageLine.append(
      el(
        "div",
        { className: "list-item-main" },
        el(
          "div",
          { className: "list-item-title" },
          `Bandwidth (${u.period}): ${u.format.total} / ${u.format.included}`,
        ),
        el(
          "div",
          { className: "list-item-meta" },
          `MCP JSON ${mcpLabel} · Media assets ${mediaLabel}`,
        ),
        el(
          "div",
          { className: "list-item-meta" },
          `${u.requestCount.toLocaleString()} MCP requests`,
          images > 0 ? ` · ${images.toLocaleString()} images` : "",
          videoSecs > 0
            ? ` · ${videoSecs.toLocaleString()} video-seconds`
            : "",
          u.softWarn ? " · approaching limit" : "",
          u.overQuota ? " · over quota" : "",
        ),
      ),
    );
    panel.append(usageLine);

    panel.append(
      el(
        "p",
        { className: "hint", style: "margin:4px 0 8px;" },
        "Chat models report tokens. Image/video Imagine APIs bill per image or per second — not tokens — so those lines show job counts (and media bytes when we can measure them).",
      ),
    );

    panel.append(tokenWindowsSection(u));

    if (u.topTools && u.topTools.length > 0) {
      panel.append(
        el(
          "p",
          { className: "hint", style: "margin:8px 0 4px;" },
          "Tools (month): ",
          u.topTools.map((t) => `${t.name} (${t.count})`).join(" · "),
        ),
      );
    }

    const refreshBtn = el(
      "button",
      {
        type: "button",
        className: "btn-ghost btn-sm",
        style: "margin-top:8px;",
        disabled: state.busy === "usage-refresh" ? true : undefined,
      },
      state.busy === "usage-refresh" ? "Refreshing…" : "Refresh usage",
    ) as HTMLButtonElement;
    refreshBtn.addEventListener("click", () => {
      void (async () => {
        state.busy = "usage-refresh";
        render();
        try {
          state.usage = await api.getUsage();
        } catch (err) {
          setError(errMessage(err));
        } finally {
          state.busy = null;
          render();
        }
      })();
    });
    panel.append(refreshBtn);
  } else {
    panel.append(
      el(
        "p",
        { className: "empty" },
        "No usage data yet. MCP tool calls are metered (bandwidth + tool counts always; tokens when the provider reports them; image/video by job count).",
      ),
    );
  }

  const actions = el("div", { className: "row-actions", style: "margin-top:12px;display:flex;flex-wrap:wrap;gap:8px;align-items:center;" });

  const monthlyBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary btn-sm",
      disabled: state.busy === "checkout" || !b.stripeConfigured,
    },
    b.stripeConfigured
      ? `Subscribe $${b.pricing.monthlyUsd}/mo`
      : "Subscribe (Stripe not configured)",
  ) as HTMLButtonElement;
  monthlyBtn.addEventListener("click", () => void runCheckout("monthly"));
  actions.append(monthlyBtn);

  const annualBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === "checkout" || !b.stripeConfigured,
    },
    b.stripeConfigured
      ? `Subscribe $${b.pricing.annualUsd}/yr`
      : `Annual $${b.pricing.annualUsd} (pending Stripe)`,
  ) as HTMLButtonElement;
  annualBtn.addEventListener("click", () => void runCheckout("annual"));
  actions.append(annualBtn);

  if (b.stripeCustomerId && b.stripeConfigured) {
    const portalBtn = el(
      "button",
      {
        type: "button",
        className: "btn-ghost btn-sm",
        disabled: state.busy === "portal",
      },
      "Billing portal",
    ) as HTMLButtonElement;
    portalBtn.addEventListener("click", () => void runPortal());
    actions.append(portalBtn);
  }

  panel.append(actions);

  // Promo redeem
  const promoRow = el("div", {
    className: "form-row",
    style: "margin-top:16px;display:flex;gap:8px;flex-wrap:wrap;align-items:end;",
  });
  const promoInput = el("input", {
    type: "text",
    className: "input",
    placeholder: "Promo code",
    autocomplete: "off",
    style: "max-width:200px;",
  }) as HTMLInputElement;
  const promoBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === "promo",
    },
    "Redeem promo",
  ) as HTMLButtonElement;
  promoBtn.addEventListener("click", () => {
    void runRedeemPromo(promoInput.value);
  });
  promoRow.append(
    el("label", { className: "field" }, el("span", null, "Promo"), promoInput),
    promoBtn,
  );
  panel.append(promoRow);

  if (b.referralCode || b.referralLink) {
    const refRow = el("div", { className: "list-item", style: "margin-top:12px;" });
    const link = b.referralLink ?? "";
    refRow.append(
      el(
        "div",
        { className: "list-item-main" },
        el(
          "div",
          { className: "list-item-title" },
          `Your referral code: ${b.referralCode ?? "—"}`,
        ),
        el(
          "div",
          { className: "list-item-meta" },
          "Friends who subscribe earn you 1 free month (max 12/year).",
        ),
      ),
    );
    if (link) {
      const copyBtn = el(
        "button",
        { type: "button", className: "btn-ghost btn-sm" },
        "Copy link",
      ) as HTMLButtonElement;
      copyBtn.addEventListener("click", () => {
        void copyText(link).then(() => {
          state.notice = "Referral link copied";
          render();
        });
      });
      refRow.append(copyBtn);
    }
    panel.append(refRow);
  }

  if (!b.stripeConfigured) {
    panel.append(
      el(
        "p",
        { className: "hint" },
        "Stripe keys not set on the server yet — checkout is scaffolded. Promo grants, referrals, and usage metering work now.",
      ),
    );
  }

  return panel;
}

async function runCheckout(plan: "monthly" | "annual"): Promise<void> {
  state.busy = "checkout";
  setError(null);
  render();
  try {
    const res = await api.startCheckout({ plan });
    if (res.checkoutUrl) {
      window.location.href = res.checkoutUrl;
      return;
    }
    setError("Checkout session created but no URL returned");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    state.busy = null;
    render();
  }
}

async function runPortal(): Promise<void> {
  state.busy = "portal";
  setError(null);
  render();
  try {
    const res = await api.openBillingPortal();
    if (res.portalUrl) {
      window.location.href = res.portalUrl;
      return;
    }
    setError("Portal URL missing");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    state.busy = null;
    render();
  }
}

async function runRedeemPromo(code: string): Promise<void> {
  if (!code.trim()) {
    setError("Enter a promo code");
    return;
  }
  state.busy = "promo";
  setError(null);
  state.notice = null;
  render();
  try {
    const res = await api.redeemPromo(code.trim());
    state.billing = res.billing;
    state.notice = `Promo ${res.promo.code} applied (${res.promo.type})`;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    state.busy = null;
    render();
  }
}

/** Owner/admin-only console at /admin. */
function renderAdminShell(): HTMLElement {
  const shell = el("div", { className: "app-shell app-shell-admin" });
  shell.append(renderTopbar({ adminPage: true }));

  if (state.error) shell.append(alertBox("error", state.error));
  if (state.notice) shell.append(alertBox("ok", state.notice));

  shell.append(
    el(
      "section",
      { className: "panel admin-intro-panel" },
      el("h2", null, "Admin"),
      el(
        "p",
        { className: "sub" },
        "Join requests, invites, users, and anomaly flags. Dashboard stays at / for MCP and accounts.",
      ),
    ),
  );

  shell.append(adminJoinRequestsPanel());
  shell.append(adminInvitesPanel());
  shell.append(adminPromosPanel());
  shell.append(adminUsersPanel());
  shell.append(adminFlagsPanel());

  return shell;
}

function adminPromosPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Promo codes"),
        el(
          "p",
          { className: "sub" },
          "free_months, free_forever_dogfood, percent_off, fixed_off",
        ),
      ),
    ),
  );

  const form = el("form", {
    className: "form-row",
    style: "display:flex;flex-wrap:wrap;gap:8px;align-items:end;margin-bottom:12px;",
  }) as HTMLFormElement;
  const codeIn = el("input", {
    type: "text",
    className: "input",
    placeholder: "CODE",
    style: "max-width:140px;",
  }) as HTMLInputElement;
  const typeIn = el("select", { className: "input" }) as HTMLSelectElement;
  for (const t of [
    "free_forever_dogfood",
    "free_months",
    "percent_off",
    "fixed_off",
  ]) {
    typeIn.append(el("option", { value: t }, t));
  }
  const valueIn = el("input", {
    type: "number",
    className: "input",
    value: "0",
    style: "max-width:80px;",
  }) as HTMLInputElement;
  const createBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary btn-sm",
      disabled: state.busy === "promo-admin" ? true : undefined,
    },
    "Create",
  ) as HTMLButtonElement;
  form.append(
    el("label", { className: "field" }, el("span", null, "Code"), codeIn),
    el("label", { className: "field" }, el("span", null, "Type"), typeIn),
    el("label", { className: "field" }, el("span", null, "Value"), valueIn),
    createBtn,
  );
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void (async () => {
      state.busy = "promo-admin";
      setError(null);
      render();
      try {
        await api.createPromo({
          code: codeIn.value.trim(),
          type: typeIn.value,
          value: Number(valueIn.value) || 0,
        });
        state.adminPromos = await api.listPromos();
        state.notice = `Promo ${codeIn.value.trim().toUpperCase()} created`;
        codeIn.value = "";
      } catch (err) {
        setError(errMessage(err));
      } finally {
        state.busy = null;
        render();
      }
    })();
  });
  panel.append(form);

  const list = el("div", { className: "list" });
  if (state.adminPromos.length === 0) {
    list.append(el("p", { className: "empty" }, "No promo codes yet."));
  } else {
    for (const p of state.adminPromos) {
      const row = el("div", { className: "list-item" });
      row.append(
        el(
          "div",
          { className: "list-item-main" },
          el(
            "div",
            { className: "list-item-title" },
            `${p.code} · ${p.type} · ${p.value}`,
          ),
          el(
            "div",
            { className: "list-item-meta" },
            `${p.active ? "active" : "disabled"} · redeemed ${p.redemptionCount}` +
              (p.maxRedemptions != null ? `/${p.maxRedemptions}` : ""),
          ),
        ),
      );
      const toggle = el(
        "button",
        {
          type: "button",
          className: "btn-ghost btn-sm",
        },
        p.active ? "Disable" : "Enable",
      ) as HTMLButtonElement;
      toggle.addEventListener("click", () => {
        void (async () => {
          try {
            await api.setPromoActive(p.code, !p.active);
            state.adminPromos = await api.listPromos();
            render();
          } catch (err) {
            setError(errMessage(err));
            render();
          }
        })();
      });
      row.append(toggle);
      list.append(row);
    }
  }
  panel.append(list);
  return panel;
}

function pendingInvitePanel(): HTMLElement {
  const panel = el("section", { className: "panel invite-pending-panel" });
  panel.append(
    el("h2", null, "Pending invite"),
    el(
      "p",
      { className: "sub" },
      state.invite.preview
        ? `Invite for ${state.invite.preview.email} is ready to accept for this session.`
        : "You have an invite token stored for this browser tab.",
    ),
  );
  if (state.invite.preview) {
    panel.append(inviteBanner());
  }
  const actions = el("div", { className: "form-actions" });
  const acceptBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary",
      disabled:
        state.busy === "invite-accept" || !state.invite.token
          ? true
          : undefined,
    },
    state.busy === "invite-accept" ? "Accepting…" : "Accept invite",
  ) as HTMLButtonElement;
  acceptBtn.addEventListener("click", () => {
    void (async () => {
      const ok = await tryAcceptPendingInvite();
      if (ok) {
        try {
          await refreshDashboard();
        } catch (err) {
          setError(errMessage(err));
        }
      }
      render();
    })();
  });
  const dismissBtn = el(
    "button",
    { type: "button", className: "btn-ghost" },
    "Dismiss",
  );
  dismissBtn.addEventListener("click", () => {
    clearSessionInviteToken();
    state.invite.token = null;
    state.invite.preview = null;
    render();
  });
  actions.append(acceptBtn, dismissBtn);
  panel.append(actions);
  return panel;
}

function alertBox(kind: "error" | "warn" | "ok" | "info", msg: string): HTMLElement {
  return el("div", { className: `alert alert-${kind}`, role: "alert" }, msg);
}

// ── Accounts ──────────────────────────────────────────────────────────────

/** Human-readable account status for badges (API may send needs_reauth). */
export function formatAccountStatus(status: string): string {
  const s = status.toLowerCase();
  if (s === "needs_reauth") return "Needs re-auth";
  if (s === "ok") return "OK";
  if (s === "error") return "Error";
  if (s === "pending") return "Pending";
  if (s === "active") return "Active";
  return status;
}

/** Days since lastVerifiedAt (null if missing/invalid). */
export function daysSinceVerified(lastVerifiedAt?: string): number | null {
  if (!lastVerifiedAt) return null;
  const t = Date.parse(lastVerifiedAt);
  if (!Number.isFinite(t)) return null;
  return (Date.now() - t) / (1000 * 60 * 60 * 24);
}

/**
 * AI account needs key update when status is needs_reauth/error.
 */
export function gmailNeedsReconnect(a: Account): boolean {
  // Legacy export name kept for tests; now means "needs key update".
  const s = a.status.toLowerCase();
  return s === "needs_reauth" || s === "error";
}

function accountsPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });

  const addBtn = el(
    "button",
    {
      type: "button",
      className: state.addAccountOpen ? "btn-secondary btn-sm" : "btn-primary btn-sm",
    },
    state.addAccountOpen ? "Cancel" : "Add AI account",
  ) as HTMLButtonElement;
  addBtn.addEventListener("click", () => {
    state.addAccountOpen = !state.addAccountOpen;
    if (state.addAccountOpen) {
      state.selectedAccountId = null;
    }
    setError(null);
    render();
  });

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Connected AIs"),
        el(
          "p",
          { className: "sub" },
          "Provider API keys for MCP tools (xAI, OpenAI, Anthropic, Google). Keys are verified on save, stored in Secrets Manager, and never shown again.",
        ),
      ),
      addBtn,
    ),
  );

  const reauth = state.accounts.filter((a) => gmailNeedsReconnect(a));
  if (reauth.length > 0) {
    const names = reauth.map((a) => `${a.label} (${a.provider})`).join(", ");
    panel.append(
      alertBox(
        "warn",
        `Needs key update: ${names}. Open the account and use Rotate API key.`,
      ),
    );
  }

  const list = el("div", { className: "list accounts-list" });
  if (state.accounts.length === 0) {
    list.append(
      el(
        "p",
        { className: "empty" },
        "No AI accounts connected yet. Click ",
        el("strong", null, "Add AI account"),
        " to paste a provider key.",
      ),
    );
  } else {
    list.append(
      el(
        "p",
        { className: "hint accounts-list-hint" },
        "Click a row to edit label, default model / effort, or rotate the key.",
      ),
    );
    for (const a of state.accounts) {
      const selected =
        state.selectedAccountId === a.id && !state.addAccountOpen;
      // Group row + inline editor so edit sits under this account, not below the list.
      const group = el("div", {
        className: selected
          ? "account-group account-group-open"
          : "account-group",
      });
      group.append(accountRow(a));
      if (selected) {
        group.append(accountEditPanel(a));
      }
      list.append(group);
    }
  }
  panel.append(list);

  // Add form — only when user clicks Add.
  if (state.addAccountOpen) {
    const formWrap = el("div", { className: "add-account-form" });
    formWrap.append(
      el("h3", { className: "panel-h3" }, "Add AI account"),
      el(
        "p",
        { className: "sub" },
        "Paste an API key from the provider console. It is verified before save and never echoed back.",
      ),
      connectAiForm(),
    );
    panel.append(formWrap);
  }

  return panel;
}

function statusBadge(status: string): HTMLElement {
  const s = status.toLowerCase();
  let cls = "badge-muted";
  if (s === "ok" || s === "active") cls = "badge-ok";
  else if (s === "needs_reauth") cls = "badge-err";
  else if (s === "error") cls = "badge-err";
  else if (s === "pending") cls = "badge-warn";
  return el("span", { className: `badge ${cls}` }, formatAccountStatus(status));
}

function accountRow(a: Account): HTMLElement {
  const selected = state.selectedAccountId === a.id;
  const row = el("div", {
    className: selected ? "list-item list-item-selected" : "list-item",
    role: "button",
    tabindex: "0",
    title: "Click to edit account",
  });
  const title = el("div", { className: "list-item-title" }, a.label || a.id);
  title.append(statusBadge(a.status));
  title.append(el("span", { className: "badge badge-muted" }, String(a.provider)));
  if (a.keyPrefix) {
    title.append(el("span", { className: "badge badge-muted" }, a.keyPrefix));
  }

  const metaParts = [
    `id ${a.id}`,
    a.defaults?.model ? `model ${a.defaults.model}` : null,
    a.defaults?.reasoningEffort
      ? `effort ${a.defaults.reasoningEffort}`
      : null,
    a.lastVerifiedAt ? `verified ${formatDate(a.lastVerifiedAt)}` : null,
    a.lastError ? `error: ${a.lastError}` : null,
    selected ? "editing" : "click to edit",
  ].filter(Boolean);

  const actions = el("div", { className: "list-item-actions" });
  if (gmailNeedsReconnect(a)) {
    const updateBtn = el(
      "button",
      {
        type: "button",
        className: "btn-primary btn-sm",
        title: "Replace API key for this account",
      },
      "Update key",
    );
    updateBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      state.addAccountOpen = false;
      state.selectedAccountId = a.id;
      render();
    });
    actions.append(updateBtn);
  }

  row.append(
    el(
      "div",
      { className: "list-item-main" },
      title,
      el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
    ),
    actions,
  );

  const select = (): void => {
    state.addAccountOpen = false;
    state.selectedAccountId =
      state.selectedAccountId === a.id ? null : a.id;
    setError(null);
    render();
  };
  row.addEventListener("click", select);
  row.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      select();
    }
  });
  return row;
}

function accountEditPanel(a: Account): HTMLElement {
  const box = el("div", {
    className: "account-edit",
    role: "region",
    "aria-label": `Edit ${a.label}`,
  });
  // Keep clicks inside the editor from toggling the row closed if the group is rewired later.
  box.addEventListener("click", (ev) => ev.stopPropagation());

  box.append(
    el(
      "p",
      { className: "hint account-edit-meta" },
      `${a.provider} · ${a.keyPrefix || "key …"} · id ${a.id}`,
    ),
  );

  const form = el("form", { className: "form-stack form-stack-compact" });
  const labelIn = el("input", {
    type: "text",
    name: "label",
    value: a.label,
    required: "true",
  }) as HTMLInputElement;
  const modelPicker = createModelPicker({
    current: a.defaults?.model,
    load: () => api.listAccountModels(a.id),
    autoLoad: true,
  });
  const effortIn = reasoningEffortSelect(a.defaults?.reasoningEffort);
  const keyIn = el("input", {
    type: "password",
    name: "apiKey",
    autocomplete: "off",
    placeholder: "new API key (leave blank to keep)",
  }) as HTMLInputElement;

  form.append(
    el("label", null, "Label", labelIn),
    modelPicker.field,
    el(
      "label",
      null,
      "Default reasoning effort",
      effortIn,
      el(
        "span",
        { className: "hint" },
        "Used when a tool call omits reasoning_effort (provider-dependent).",
      ),
    ),
    el("label", null, "Rotate API key", withShowPassword(keyIn)),
  );

  const closeEditor = (): void => {
    state.selectedAccountId = null;
    setError(null);
    render();
  };

  const actions = el("div", { className: "form-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary",
      disabled: state.busy === `edit-${a.id}` ? true : undefined,
    },
    state.busy === `edit-${a.id}` ? "Saving…" : "Save",
  );
  const cancelBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary",
      disabled: state.busy === `edit-${a.id}` ? true : undefined,
    },
    "Cancel",
  );
  const verifyBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary",
      disabled: state.busy === `verify-${a.id}` ? true : undefined,
    },
    state.busy === `verify-${a.id}` ? "…" : "Re-verify",
  );
  const delBtn = el(
    "button",
    {
      type: "button",
      className: "btn-danger",
      disabled: state.busy === `del-acc-${a.id}` ? true : undefined,
    },
    "Disconnect",
  );
  actions.append(saveBtn, cancelBtn, verifyBtn, delBtn);
  form.append(actions);

  cancelBtn.addEventListener("click", () => {
    closeEditor();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void (async () => {
      const label = labelIn.value.trim();
      if (!label) {
        setError("Label is required");
        render();
        return;
      }
      const defaults = buildAccountDefaults(
        modelPicker.getValue(),
        effortIn.value,
      );
      const newKey = keyIn.value.trim();
      const sameLabel = label === a.label;
      const sameModel =
        (defaults.model ?? "") === (a.defaults?.model ?? "").trim();
      const sameEffort =
        (defaults.reasoningEffort ?? "") ===
        (a.defaults?.reasoningEffort ?? "");
      // No-op save: just collapse the inline editor.
      if (!newKey && sameLabel && sameModel && sameEffort) {
        closeEditor();
        return;
      }
      if (newKey) {
        const rotated = await onRotateKey(a.id, newKey);
        if (!rotated) return;
      }
      const ok = await onUpdateAccount(a.id, { label, defaults });
      if (ok) {
        state.selectedAccountId = null;
        render();
      }
    })();
  });
  verifyBtn.addEventListener("click", () => {
    void onVerifyAccount(a.id);
  });
  delBtn.addEventListener("click", () => {
    if (
      !window.confirm(
        `Disconnect "${a.label}" (${a.provider})? MCP tools will no longer use this account.`,
      )
    ) {
      return;
    }
    void onDeleteAccount(a.id);
  });

  box.append(form);
  return box;
}

function connectAiForm(): HTMLElement {
  const form = el("form", { className: "form-stack" });
  const provider = el("select", { name: "provider", required: "true" }) as HTMLSelectElement;
  for (const [value, label] of [
    ["xai", "xAI (Grok)"],
    ["openai", "OpenAI"],
    ["anthropic", "Anthropic"],
    ["google", "Google (Gemini)"],
  ] as const) {
    provider.append(el("option", { value }, label));
  }
  const labelIn = el("input", {
    type: "text",
    name: "label",
    required: "true",
    placeholder: "e.g. Personal Grok",
  }) as HTMLInputElement;
  const keyIn = el("input", {
    type: "password",
    name: "apiKey",
    required: "true",
    autocomplete: "off",
    placeholder: "API key",
  }) as HTMLInputElement;
  const modelPicker = createModelPicker({
    current: undefined,
    load: async () => {
      const key = keyIn.value.trim();
      if (key.length < 8) {
        throw new Error("Paste an API key first, then load models");
      }
      return api.previewModels(provider.value, key);
    },
    autoLoad: false,
  });
  const effortIn = reasoningEffortSelect(undefined);

  form.append(
    el("label", null, "Provider", provider),
    el("label", null, "Label", labelIn),
    el("label", null, "API key", withShowPassword(keyIn)),
    modelPicker.field,
    el(
      "label",
      null,
      "Default reasoning effort",
      effortIn,
      el(
        "span",
        { className: "hint" },
        "Applied when MCP tools omit reasoning_effort. Provider support varies.",
      ),
    ),
  );

  // After key entry, pull live models from the provider (xAI, OpenAI, …).
  const tryLoadModels = () => {
    if (keyIn.value.trim().length >= 8) void modelPicker.reload();
  };
  keyIn.addEventListener("blur", tryLoadModels);
  keyIn.addEventListener("change", tryLoadModels);
  provider.addEventListener("change", () => {
    modelPicker.clear();
    tryLoadModels();
  });

  const submit = el(
    "button",
    {
      type: "submit",
      className: "btn-primary",
      disabled: state.busy === "add-ai" ? true : undefined,
    },
    state.busy === "add-ai" ? "Verifying…" : "Connect",
  );
  form.append(el("div", { className: "form-actions" }, submit));

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const body: api.CreateAiAccountBody = {
      provider: provider.value,
      label: labelIn.value.trim(),
      apiKey: keyIn.value.trim(),
    };
    const defaults = buildAccountDefaults(
      modelPicker.getValue(),
      effortIn.value,
    );
    if (Object.keys(defaults).length > 0) body.defaults = defaults;
    void onAddAiAccount(body, () => {
      labelIn.value = "";
      keyIn.value = "";
      effortIn.value = "";
      modelPicker.clear();
    });
  });

  return form;
}

/** Human-readable option label for model ids (esp. Anthropic Opus 5). */
function friendlyModelLabel(id: string, kind?: string): string {
  const map: Record<string, string> = {
    "claude-opus-5-5": "Claude Opus 5.5",
    "claude-sonnet-5-5": "Claude Sonnet 5.5",
    "claude-fable-5-1": "Claude Fable 5.1",
    "claude-opus-5": "Claude Opus 5",
    "claude-sonnet-5": "Claude Sonnet 5",
    "claude-fable-5": "Claude Fable 5",
    "claude-opus-4-7": "Claude Opus 4.7",
    "claude-opus-4-5": "Claude Opus 4.5",
    "claude-haiku-4-5": "Claude Haiku 4.5",
    "claude-haiku-4-5-20251001": "Claude Haiku 4.5 (20251001)",
    "claude-opus-4-8": "Claude Opus 4.8",
    "claude-opus-4-6": "Claude Opus 4.6",
    "claude-sonnet-4-6": "Claude Sonnet 4.6",
  };
  const base = map[id] ?? id;
  if (kind && kind !== "language") return `${base} · ${kind} (${id})`;
  return map[id] ? `${base} (${id})` : id;
}

/** Build account defaults from form fields (omits empty). */
function buildAccountDefaults(
  modelRaw: string,
  effortRaw: string,
): AccountDefaults {
  const defaults: AccountDefaults = {};
  const model = modelRaw.trim();
  if (model) defaults.model = model;
  if (
    effortRaw === "none" ||
    effortRaw === "low" ||
    effortRaw === "medium" ||
    effortRaw === "high"
  ) {
    defaults.reasoningEffort = effortRaw;
  }
  return defaults;
}

/** Select for default reasoning effort (none / low / medium / high). */
function reasoningEffortSelect(
  current: AccountDefaults["reasoningEffort"] | undefined,
): HTMLSelectElement {
  const sel = el("select", {
    name: "reasoningEffort",
  }) as HTMLSelectElement;
  const options: { value: string; label: string }[] = [
    { value: "", label: "Provider default (unset)" },
    { value: "none", label: "None" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
  ];
  for (const o of options) {
    const opt = el("option", { value: o.value }, o.label) as HTMLOptionElement;
    if ((current ?? "") === o.value) opt.selected = true;
    sel.append(opt);
  }
  return sel;
}

type ModelPicker = {
  field: HTMLElement;
  getValue: () => string;
  reload: () => Promise<void>;
  clear: () => void;
};

/**
 * Default-model control: live dropdown from provider listModels + optional custom id.
 */
function createModelPicker(opts: {
  current?: string | undefined;
  load: () => Promise<api.ListModelsResponse>;
  autoLoad: boolean;
}): ModelPicker {
  const current = (opts.current ?? "").trim();
  const wrap = el("div", { className: "model-picker" });
  const status = el(
    "div",
    { className: "hint model-picker-status" },
    "Load models from the provider API.",
  );
  const select = el("select", {
    name: "modelSelect",
    className: "model-picker-select",
  }) as HTMLSelectElement;
  select.append(
    el(
      "option",
      { value: "" },
      current ? `(current) ${current}` : "No default (provider auto)",
    ) as HTMLOptionElement,
  );
  if (current) {
    const curOpt = el(
      "option",
      { value: current },
      `${current} (saved)`,
    ) as HTMLOptionElement;
    curOpt.selected = true;
    select.append(curOpt);
  }
  const custom = el("input", {
    type: "text",
    name: "modelCustom",
    className: "model-picker-custom",
    placeholder: "Or type a model id…",
    value: "",
    autocomplete: "off",
  }) as HTMLInputElement;

  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
    },
    "Load models",
  ) as HTMLButtonElement;

  const row = el("div", { className: "model-picker-row" }, select, refreshBtn);
  wrap.append(row, custom, status);

  const field = el(
    "label",
    { className: "field" },
    "Default model",
    wrap,
    el(
      "span",
      { className: "hint" },
      "Pulled live from the provider (e.g. xAI GET /v1/models). Select one or type a custom id.",
    ),
  );

  let loading = false;
  const reload = async () => {
    if (loading) return;
    loading = true;
    refreshBtn.disabled = true;
    status.textContent = "Loading models…";
    try {
      const res = await opts.load();
      const models = res.models ?? [];
      // Rebuild options, keep selection when possible.
      const prev =
        custom.value.trim() || select.value.trim() || current || "";
      clear(select);
      select.append(
        el(
          "option",
          { value: "" },
          "No default (provider auto)",
        ) as HTMLOptionElement,
      );
      // Language first; prefer Opus/Sonnet-style ids near the top for Anthropic.
      const preferred = [
        "claude-opus-5-5",
        "claude-sonnet-5-5",
        "claude-fable-5-1",
        "claude-opus-5",
        "claude-sonnet-5",
        "claude-fable-5",
        "claude-haiku-4-5",
      ];
      const sorted = [...models].sort((a, b) => {
        const ka = a.kind === "language" ? 0 : 1;
        const kb = b.kind === "language" ? 0 : 1;
        if (ka !== kb) return ka - kb;
        const ia = preferred.indexOf(a.id);
        const ib = preferred.indexOf(b.id);
        const ra = ia === -1 ? 50 : ia;
        const rb = ib === -1 ? 50 : ib;
        if (ra !== rb) return ra - rb;
        return a.id.localeCompare(b.id);
      });
      const seen = new Set<string>();
      for (const m of sorted) {
        if (seen.has(m.id)) continue;
        seen.add(m.id);
        const label = friendlyModelLabel(m.id, m.kind);
        select.append(el("option", { value: m.id }, label) as HTMLOptionElement);
      }
      if (prev && !seen.has(prev)) {
        select.append(
          el("option", { value: prev }, `${prev} (custom)`) as HTMLOptionElement,
        );
      }
      if (prev) {
        select.value = prev;
        if (custom.value && custom.value.trim() === prev) {
          /* keep custom box */
        } else if (seen.has(prev) || prev === current) {
          custom.value = "";
        }
      }
      status.textContent =
        models.length === 0
          ? "Provider returned no models."
          : `${models.length} model${models.length === 1 ? "" : "s"} from ${res.provider}.`;
    } catch (err) {
      status.textContent = `Could not load models: ${errMessage(err)}`;
    } finally {
      loading = false;
      refreshBtn.disabled = false;
    }
  };

  refreshBtn.addEventListener("click", (ev) => {
    ev.preventDefault();
    void reload();
  });
  // Custom text overrides select when non-empty.
  custom.addEventListener("input", () => {
    if (custom.value.trim()) {
      // leave select as-is; getValue prefers custom
    }
  });

  if (opts.autoLoad) {
    void reload();
  }

  return {
    field,
    getValue: () => {
      const c = custom.value.trim();
      if (c) return c;
      return select.value.trim();
    },
    reload,
    clear: () => {
      clear(select);
      select.append(
        el(
          "option",
          { value: "" },
          "No default (provider auto)",
        ) as HTMLOptionElement,
      );
      custom.value = "";
      status.textContent =
        "Paste an API key, then click Load models (or tab out of the key field).";
    },
  };
}

const SCOPE_HELP: Record<PatScope, string> = {
  read: "Discovery + read tools (list_accounts, list_models, search).",
  send: "Legacy scope; use full for AI tool writes in Phase A.",
  full: "All MCP tools (chat, generate, files, agent, …).",
};

function mcpAccessPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  const cfg = mcpSetupConfig();
  const scopesEnforce = state.features["pats.scopesEnforce"] === true;

  panel.append(
    el("h2", null, "MCP access"),
    el(
      "p",
      { className: "sub" },
      "Paste this URL into Claude, Grok, or any MCP client. OAuth is automatic.",
    ),
  );

  const mcpClientId =
    cfg.mcpClientId ||
    "182stq6coiojuad6a61jncrkh5"; /* stage default from CDK */

  // Always-visible: server URL + copy only.
  const urlRow = el("div", { className: "mcp-url-row" });
  const urlCode = el(
    "code",
    { className: "mcp-url-value", title: cfg.mcpResource },
    cfg.mcpResource,
  );
  const copyUrlBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary btn-sm mcp-url-copy",
    },
    "Copy",
  ) as HTMLButtonElement;
  copyUrlBtn.addEventListener("click", async () => {
    const ok = await copyText(cfg.mcpResource);
    setNotice(ok ? "MCP URL copied" : "Could not copy");
    render();
  });
  urlRow.append(urlCode, copyUrlBtn);
  panel.append(urlRow);

  // Setup steps collapsed behind a disclosure link.
  const howTo = el("details", { className: "mcp-howto" });
  howTo.append(el("summary", null, "How to connect Claude / Grok"));
  const howBody = el("div", { className: "mcp-howto-body" });
  howBody.append(
    el(
      "p",
      { className: "hint" },
      "The MCP server URL is simply ",
      el("strong", null, "https://mcpto.ai"),
      " — the same address as this website.",
    ),
    el(
      "ol",
      { className: "connect-steps" },
      el(
        "li",
        null,
        "Claude Desktop: ",
        el("strong", null, "Customize → Connectors → + → Add custom connector"),
        " (or Settings → Connectors).",
      ),
      el(
        "li",
        null,
        "Remote MCP server URL: paste the URL above (already on your clipboard if you clicked Copy).",
      ),
      el(
        "li",
        null,
        "Optional Advanced settings → OAuth Client ID: ",
        el("code", { className: "inline-code" }, mcpClientId),
        " ",
        el(
          "button",
          {
            type: "button",
            className: "btn-ghost btn-sm",
            "data-copy": mcpClientId,
          },
          "Copy",
        ),
        " · Client Secret: leave ",
        el("strong", null, "blank"),
        " (public PKCE client).",
      ),
      el(
        "li",
        null,
        "Click Connect / Add, then sign in at ",
        el(
          "code",
          { className: "inline-code" },
          (cfg.authHostedUiBase || "https://auth.mcpto.ai").replace(
            /^https?:\/\//,
            "",
          ),
        ),
        " with this same account.",
      ),
    ),
    el(
      "p",
      { className: "hint" },
      "If the connector UI still fails, use a personal access token (below) with Claude Desktop config via mcp-remote.",
    ),
  );
  if (state.features["docs.portal"]) {
    howBody.append(
      el(
        "p",
        { className: "hint" },
        el("a", { href: "/docs" }, "Config snippets and bridge docs → /docs"),
      ),
    );
  }
  howBody.querySelectorAll("[data-copy]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const v = (btn as HTMLElement).getAttribute("data-copy") ?? "";
      const ok = await copyText(v);
      setNotice(ok ? "Copied" : "Could not copy");
      render();
    });
  });
  howTo.append(howBody);
  panel.append(howTo);

  panel.append(
    el("h3", { className: "panel-h3" }, "Active clients"),
    el(
      "p",
      { className: "hint" },
      "OAuth clients (Claude, Grok, …) appear after they connect to MCP. Personal tokens appear when minted. Disconnect revokes access immediately.",
    ),
  );

  if (state.minted) {
    panel.append(mintedSecretBox(state.minted));
  }

  const list = el("div", { className: "list" });
  const active = state.mcpClients.filter((c) => c.status === "active");
  const inactive = state.mcpClients.filter((c) => c.status !== "active");
  if (active.length === 0 && inactive.length === 0) {
    list.append(
      el(
        "p",
        { className: "empty" },
        "No clients yet. Connect Claude or Grok with the server URL above, or mint a personal token below. After OAuth, use the connector once so it shows up here.",
      ),
    );
  } else {
    for (const c of active) {
      list.append(mcpClientRow(c));
    }
    for (const c of inactive) {
      list.append(mcpClientRow(c));
    }
  }
  panel.append(list);

  const advanced = el("details", { className: "mcp-advanced" });
  advanced.append(
    el(
      "summary",
      null,
      "Optional: personal access token (stdio bridge / automation)",
    ),
  );
  const advBody = el("div", { className: "mcp-advanced-body" });
  advBody.append(
    el(
      "p",
      { className: "hint" },
      "Use when a client cannot complete browser OAuth. Max 10 active; default TTL 365 days. Token shown once.",
    ),
  );
  if (!scopesEnforce) {
    advBody.append(
      alertBox(
        "info",
        "Scope enforcement is off — scopes are stored but MCP does not reject tools based on them yet.",
      ),
    );
  }

  const form = el("form", { className: "form-stack" });
  const name = el("input", {
    type: "text",
    name: "name",
    required: true,
    placeholder: "e.g. Stdio bridge · laptop",
    maxlength: "128",
    autocomplete: "off",
  }) as HTMLInputElement;
  const days = el("input", {
    type: "number",
    name: "expiresInDays",
    value: "365",
    min: "1",
    max: "3650",
  }) as HTMLInputElement;

  const scopeChecks: Record<PatScope, HTMLInputElement> = {
    read: scopeCheckbox("read", false),
    send: scopeCheckbox("send", false),
    full: scopeCheckbox("full", true),
  };

  const scopeField = el("fieldset", { className: "scope-fieldset" });
  scopeField.append(
    el("legend", null, "Scopes"),
    el(
      "p",
      { className: "hint" },
      "Prefer least privilege. full replaces other scopes.",
    ),
  );
  for (const scope of ["read", "send", "full"] as PatScope[]) {
    const row = el("label", {
      className: "scope-option",
      title: SCOPE_HELP[scope],
    });
    const labelText = el("span", { className: "scope-label" }, scope);
    if (scope === "read") {
      labelText.append(
        el(
          "span",
          {
            className: "scope-tip",
            title:
              "list_drafts and reading draft message bodies are under read (intentional — research PATs can see drafts).",
          },
          " · includes drafts",
        ),
      );
    }
    row.append(scopeChecks[scope], labelText);
    const help = el("span", { className: "scope-help" }, SCOPE_HELP[scope]);
    scopeField.append(el("div", { className: "scope-row" }, row, help));
  }

  const syncExclusive = (changed: PatScope): void => {
    if (changed === "full" && scopeChecks.full.checked) {
      scopeChecks.read.checked = false;
      scopeChecks.send.checked = false;
    } else if (changed !== "full" && scopeChecks[changed].checked) {
      scopeChecks.full.checked = false;
    }
  };
  for (const scope of ["read", "send", "full"] as PatScope[]) {
    scopeChecks[scope].addEventListener("change", () => {
      syncExclusive(scope);
    });
  }

  form.append(
    el(
      "div",
      { className: "form-row" },
      el("label", { className: "field" }, "Client name", name),
      el("label", { className: "field" }, "Expires in days", days),
    ),
    scopeField,
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "mint" ? true : undefined,
        },
        state.busy === "mint" ? "Creating…" : "Create token",
      ),
    ),
  );

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const n = name.value.trim();
    const d = Number(days.value) || 365;
    const scopes = selectedScopes(scopeChecks);
    if (scopes.length === 0) {
      setError("Select at least one scope (read, send, or full)");
      render();
      return;
    }
    void onMint(n, d, scopes, () => {
      name.value = "";
      scopeChecks.read.checked = false;
      scopeChecks.send.checked = false;
      scopeChecks.full.checked = true;
    });
  });

  advBody.append(form);
  advanced.append(advBody);
  panel.append(advanced);

  return panel;
}

function scopeCheckbox(scope: PatScope, checked: boolean): HTMLInputElement {
  return el("input", {
    type: "checkbox",
    name: "scopes",
    value: scope,
    checked: checked ? true : undefined,
  }) as HTMLInputElement;
}

function selectedScopes(
  checks: Record<PatScope, HTMLInputElement>,
): PatScope[] {
  if (checks.full.checked) return ["full"];
  const out: PatScope[] = [];
  if (checks.read.checked) out.push("read");
  if (checks.send.checked) out.push("send");
  return out;
}

function scopeBadges(scopes: PatScope[]): HTMLElement[] {
  return scopes.map((s) => {
    let cls = "badge badge-muted";
    if (s === "full") cls = "badge badge-ok";
    else if (s === "send") cls = "badge badge-warn";
    else if (s === "read") cls = "badge badge-muted";
    const title =
      s === "read"
        ? "read — includes list_drafts and reading draft bodies"
        : SCOPE_HELP[s];
    return el("span", { className: cls, title }, s);
  });
}

function mintedSecretBox(m: MintedPat): HTMLElement {
  const box = el("div", { className: "secret-box" });
  const scopes = scopesOf(m);
  const metaLine = el("div", { className: "list-item-meta" }, `${m.name} · ${m.tokenPrefix}…`);
  for (const b of scopeBadges(scopes)) metaLine.append(b);
  box.append(
    alertBox(
      "warn",
      "Copy this token now. It will not be shown again. Treat it like a password.",
    ),
    metaLine,
    el("code", null, m.token),
    el(
      "div",
      { className: "form-actions" },
      el("button", { type: "button", className: "btn-primary btn-sm" }, "Copy token"),
      el("button", { type: "button", className: "btn-secondary btn-sm" }, "Dismiss"),
    ),
  );
  const [copyBtn, dismissBtn] = box.querySelectorAll("button");
  copyBtn?.addEventListener("click", async () => {
    const ok = await copyText(m.token);
    setNotice(ok ? "Token copied to clipboard" : "Could not copy — select the token manually");
    render();
  });
  dismissBtn?.addEventListener("click", () => {
    state.minted = null;
    setNotice(null);
    render();
  });
  return box;
}

/** Active MCP client row (OAuth or PAT) with disconnect. */
function mcpClientRow(c: McpClient): HTMLElement {
  const row = el("div", { className: "list-item" });
  const revoked = c.status === "revoked";
  const title = el("div", { className: "list-item-title" }, c.name);
  title.append(
    el(
      "span",
      { className: "badge badge-muted" },
      c.kind === "oauth" ? "oauth" : "token",
    ),
  );
  if (revoked) {
    title.append(el("span", { className: "badge badge-err" }, "disconnected"));
  }
  if (c.scopes && c.scopes.length > 0) {
    for (const b of scopeBadges(c.scopes)) title.append(b);
  }
  const metaParts = [
    `created ${formatDate(c.createdAt)}`,
    c.lastUsedAt
      ? `last used ${formatDate(c.lastUsedAt)}`
      : "never used on MCP",
  ];
  if (c.product) metaParts.unshift(c.product);
  row.append(
    el(
      "div",
      { className: "list-item-main" },
      title,
      el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
    ),
  );
  if (!revoked) {
    const actions = el("div", { className: "list-item-actions" });
    const btn = el(
      "button",
      {
        type: "button",
        className: "btn-danger btn-sm",
        disabled: state.busy === `disc-${c.id}` ? true : undefined,
      },
      state.busy === `disc-${c.id}` ? "…" : "Disconnect",
    ) as HTMLButtonElement;
    btn.addEventListener("click", () => {
      const kindLabel = c.kind === "oauth" ? "OAuth client" : "token";
      if (
        !confirm(
          `Disconnect “${c.name}”? This ${kindLabel} will lose MCP access immediately.`,
        )
      ) {
        return;
      }
      void onDisconnectMcpClient(c.id);
    });
    actions.append(btn);
    row.append(actions);
  }
  return row;
}

// ── Compose attachments (SPA upload sessions, not MCP) ────────────────────

// ── Audit log (own events) ────────────────────────────────────────────────

function formatMeta(meta: Record<string, unknown> | undefined): string {
  if (!meta || Object.keys(meta).length === 0) return "—";
  try {
    return JSON.stringify(meta);
  } catch {
    return "—";
  }
}

function auditModelLabel(ev: AuditEvent): string {
  // Prefer first-class field from API; fall back to meta.model (older rows).
  let raw = "";
  if (typeof ev.model === "string" && ev.model.trim()) {
    raw = ev.model.trim();
  } else if (ev.meta && typeof ev.meta === "object") {
    const model = ev.meta["model"];
    if (typeof model === "string" && model.trim()) raw = model.trim();
  }
  if (!raw) return "—";
  // Prefer friendly display when we know the id (still keep id for obscure models).
  return friendlyModelLabel(raw);
}

function auditTableRow(ev: AuditEvent): HTMLElement {
  const tr = el("tr", { className: "audit-tr" });
  // Meta without model (shown in its own column).
  let metaView: Record<string, unknown> | undefined;
  if (ev.meta && typeof ev.meta === "object") {
    const { model: _m, ...rest } = ev.meta as Record<string, unknown> & {
      model?: unknown;
    };
    metaView = Object.keys(rest).length > 0 ? rest : undefined;
  }
  tr.append(
    el("td", { className: "audit-td audit-td-time" }, formatDate(ev.at)),
    el("td", { className: "audit-td audit-td-action" }, ev.action),
    el("td", { className: "audit-td" }, ev.tool ?? "—"),
    el(
      "td",
      { className: "audit-td audit-td-mono" },
      auditModelLabel(ev),
    ),
    el(
      "td",
      { className: "audit-td audit-td-meta" },
      formatMeta(metaView),
    ),
  );
  return tr;
}

function auditPanel(): HTMLElement {
  const count = state.auditEvents.length;
  const panel = el("section", { className: "panel" });

  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === "audit-refresh" ? true : undefined,
    },
    state.busy === "audit-refresh" ? "…" : "Refresh",
  ) as HTMLButtonElement;
  refreshBtn.addEventListener("click", () => {
    void onRefreshAudit();
  });

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Activity log"),
        el(
          "p",
          { className: "sub" },
          "Control-plane and MCP events (no message bodies). Newest first.",
        ),
      ),
      el("div", { className: "panel-header-actions" }, refreshBtn),
    ),
  );

  const body = el("div");

  if (count === 0) {
    body.append(
      el(
        "p",
        { className: "empty" },
        "No audit events yet. Connecting accounts, minting tokens, and MCP tools will appear here.",
      ),
    );
  } else {
    const table = el("table", { className: "audit-table" });
    const thead = el("thead");
    thead.append(
      el(
        "tr",
        null,
        el("th", null, "Time"),
        el("th", null, "Action"),
        el("th", null, "Tool"),
        el("th", null, "Model"),
        el("th", null, "Meta"),
      ),
    );
    const tbody = el("tbody");
    for (const ev of state.auditEvents) {
      tbody.append(auditTableRow(ev));
    }
    table.append(thead, tbody);
    const scroll = el("div", { className: "audit-table-scroll" }, table);
    body.append(scroll);
  }

  if (state.auditNextCursor) {
    const more = el(
      "div",
      { className: "form-actions audit-more" },
      el(
        "button",
        {
          type: "button",
          className: "btn-secondary btn-sm",
          disabled: state.busy === "audit-more" ? true : undefined,
        },
        state.busy === "audit-more" ? "Loading…" : "Load more",
      ),
    );
    more.querySelector("button")?.addEventListener("click", () => {
      void onLoadMoreAudit();
    });
    body.append(more);
  }

  panel.append(body);
  return panel;
}

// ── Admin anomaly flags ───────────────────────────────────────────────────

function severityBadge(severity: string): HTMLElement {
  const s = severity.toLowerCase();
  let cls = "badge badge-muted";
  if (s === "high") cls = "badge badge-err";
  else if (s === "medium") cls = "badge badge-warn";
  else if (s === "low") cls = "badge badge-muted";
  return el("span", { className: cls }, severity);
}

function flagStatusBadge(status: string): HTMLElement {
  const s = status.toLowerCase();
  const cls = s === "open" ? "badge badge-warn" : "badge badge-ok";
  return el("span", { className: cls }, status);
}

function adminFlagRow(f: AdminFlag): HTMLElement {
  const row = el("div", { className: "list-item" });
  const title = el("div", { className: "list-item-title" }, f.reason);
  title.append(severityBadge(String(f.severity)));
  title.append(flagStatusBadge(String(f.status)));

  const metaParts = [
    `id ${f.flagId}`,
    formatDate(f.createdAt),
    f.userId ? `user ${f.userId}` : null,
    f.ackedAt ? `acked ${formatDate(f.ackedAt)}` : null,
  ].filter(Boolean) as string[];
  const metaStr = formatMeta(f.meta);
  if (metaStr) metaParts.push(metaStr);

  const actions = el("div", { className: "list-item-actions" });
  if (String(f.status).toLowerCase() === "open") {
    const ackBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `ack-flag-${f.flagId}` ? true : undefined,
      },
      state.busy === `ack-flag-${f.flagId}` ? "…" : "Ack",
    );
    ackBtn.addEventListener("click", () => {
      void onAckFlag(f.flagId);
    });
    actions.append(ackBtn);
  }

  row.append(
    el(
      "div",
      { className: "list-item-main" },
      title,
      el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
    ),
    actions,
  );
  return row;
}

// ── Admin: join requests + invites + users ────────────────────────────────

function adminJoinRequestsPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-join-requests",
  });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Admin · Join the Test"),
        el(
          "p",
          { className: "sub" },
          "Public waitlist from /join. Approve + invite sends SES + Cognito mail. Gmail applicants need Google OAuth Test users (button opens console).",
        ),
      ),
      el(
        "a",
        {
          href: "/join",
          className: "btn-ghost btn-sm",
          target: "_blank",
          rel: "noopener",
        },
        "Open /join",
      ),
    ),
  );

  // Pending Gmail count banner
  const pendingGmail = state.joinRequests.filter(
    (r) =>
      r.status === "pending" &&
      r.wantsGmail &&
      r.googleTestUserStatus !== "added",
  );
  if (pendingGmail.length > 0) {
    panel.append(
      alertBox(
        "warn",
        `${pendingGmail.length} pending Gmail applicant(s) need Google Test users before Connect Gmail works.`,
      ),
    );
  }

  const filters = el("div", { className: "filter-row" });
  for (const opt of [
    { id: "pending" as const, label: "Pending" },
    { id: "invited" as const, label: "Invited" },
    { id: "dismissed" as const, label: "Dismissed" },
    { id: "all" as const, label: "All" },
  ]) {
    const active = state.joinRequestStatusFilter === opt.id;
    const btn = el(
      "button",
      {
        type: "button",
        className: active ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === "join-refresh" ? true : undefined,
      },
      opt.label,
    );
    btn.addEventListener("click", () => {
      if (state.joinRequestStatusFilter === opt.id) return;
      state.joinRequestStatusFilter = opt.id;
      void onRefreshJoinRequests();
    });
    filters.append(btn);
  }
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "join-refresh" ? true : undefined,
    },
    state.busy === "join-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshJoinRequests();
  });
  filters.append(refreshBtn);
  panel.append(filters);

  const list = el("div", { className: "list" });
  if (state.joinRequests.length === 0) {
    list.append(
      el("p", { className: "empty" }, "No join requests for this filter."),
    );
  } else {
    for (const row of state.joinRequests) {
      list.append(joinRequestRow(row));
    }
  }
  panel.append(list);
  return panel;
}

function joinRequestRow(row: JoinRequestRow): HTMLElement {
  const elRow = el("div", { className: "list-row" });
  const statusClass =
    row.status === "pending"
      ? "badge badge-warn"
      : row.status === "invited"
        ? "badge badge-ok"
        : "badge badge-muted";
  const wantsGmail = row.wantsGmail === true;
  const gmailStatus = row.googleTestUserStatus || (wantsGmail ? "needed" : "not_needed");
  const gmailBadgeClass =
    gmailStatus === "added"
      ? "badge badge-ok"
      : gmailStatus === "needed" || gmailStatus === "manual"
        ? "badge badge-warn"
        : "badge badge-muted";
  const main = el("div", { className: "list-main" });
  main.append(
    el(
      "div",
      { className: "list-title" },
      row.email,
      " ",
      el("span", { className: statusClass }, row.status),
      wantsGmail
        ? el("span", { className: "badge badge-warn" }, "Gmail")
        : el("span", { className: "badge badge-muted" }, "no Gmail"),
      wantsGmail
        ? el("span", { className: gmailBadgeClass }, `Google: ${gmailStatus}`)
        : null,
      row.name ? el("span", { className: "badge badge-muted" }, row.name) : null,
    ),
    el(
      "div",
      { className: "list-meta" },
      [
        row.company ? row.company : null,
        wantsGmail && row.gmailEmail && row.gmailEmail !== row.email
          ? `Google: ${row.gmailEmail}`
          : null,
        `Requested ${formatDate(row.createdAt)}`,
        row.note
          ? row.note.slice(0, 120) + (row.note.length > 120 ? "…" : "")
          : null,
        row.googleTestUserNote
          ? row.googleTestUserNote.slice(0, 80)
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
    ),
  );
  elRow.append(main);

  const actions = el("div", { className: "row-actions" });
  if (row.status === "pending" && state.features["invites.productized"]) {
    const inviteBtn = el(
      "button",
      {
        type: "button",
        className: "btn-primary btn-sm",
        disabled: state.busy === `join-invite-${row.email}` ? true : undefined,
      },
      state.busy === `join-invite-${row.email}`
        ? "…"
        : wantsGmail
          ? "Approve + invite"
          : "Invite",
    );
    inviteBtn.addEventListener("click", () => {
      void onInviteFromJoinRequest(row);
    });
    actions.append(inviteBtn);
  }
  if (wantsGmail && gmailStatus !== "added") {
    const googleBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled:
          state.busy === `join-google-${row.email}` ? true : undefined,
      },
      state.busy === `join-google-${row.email}` ? "…" : "Google test user",
    );
    googleBtn.addEventListener("click", () => {
      void onAddGoogleTestUser(row.email);
    });
    actions.append(googleBtn);
  }
  if (row.status === "pending") {
    const dismissBtn = el(
      "button",
      {
        type: "button",
        className: "btn-ghost btn-sm",
        disabled: state.busy === `join-dismiss-${row.email}` ? true : undefined,
      },
      "Dismiss",
    );
    dismissBtn.addEventListener("click", () => {
      void onPatchJoinRequest(row.email, { status: "dismissed" });
    });
    actions.append(dismissBtn);
  } else if (row.status === "dismissed") {
    const restoreBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `join-restore-${row.email}` ? true : undefined,
      },
      "Restore",
    );
    restoreBtn.addEventListener("click", () => {
      void onPatchJoinRequest(row.email, { status: "pending" });
    });
    actions.append(restoreBtn);
  }
  elRow.append(actions);
  return elRow;
}

async function onRefreshJoinRequests(): Promise<void> {
  setBusy("join-refresh");
  setError(null);
  try {
    const status =
      state.joinRequestStatusFilter === "all"
        ? undefined
        : state.joinRequestStatusFilter;
    state.joinRequests = await api.listJoinRequests(
      status ? { status } : undefined,
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onPatchJoinRequest(
  email: string,
  body: {
    status?: "pending" | "invited" | "dismissed";
    googleTestUserStatus?: string;
  },
): Promise<void> {
  const status = body.status;
  const busyKey =
    status === "dismissed"
      ? `join-dismiss-${email}`
      : status === "pending"
        ? `join-restore-${email}`
        : `join-invite-${email}`;
  setBusy(busyKey);
  setError(null);
  try {
    await api.patchJoinRequest(email, body);
    setNotice(
      status === "dismissed"
        ? `Dismissed ${email}`
        : status === "pending"
          ? `Restored ${email}`
          : `Marked ${email} invited`,
    );
    await onRefreshJoinRequests();
  } catch (err) {
    setError(errMessage(err));
    setBusy(null);
    render();
  }
}

/** Best-effort Google OAuth test-user add; always surfaces console URL when needed. */
async function onAddGoogleTestUser(email: string): Promise<void> {
  setBusy(`join-google-${email}`);
  setError(null);
  try {
    const res = await api.addJoinRequestGoogleTestUser(email);
    const g = res.google;
    if (g?.consoleUrl && g.status !== "added") {
      const ok = await copyText(res.gmailEmail || res.email);
      setNotice(
        g.note
          ? `${g.note}${ok ? " Email copied." : ""}`
          : `Add ${res.gmailEmail || res.email} under Google Auth Platform → Audience → Test users.${ok ? " Email copied." : ""}`,
      );
      window.open(g.consoleUrl, "_blank", "noopener,noreferrer");
    } else if (g?.status === "added") {
      setNotice(`Added ${res.gmailEmail || res.email} to Google test users.`);
    } else {
      setNotice(`Google test user status: ${g?.status ?? "unknown"}`);
    }
    await onRefreshJoinRequests();
  } catch (err) {
    setError(errMessage(err));
    setBusy(null);
    render();
  }
}

/**
 * Approve: create productized invite, mark invited, and for Gmail applicants
 * kick off Google test-user enrollment (console deep-link when manual).
 */
async function onInviteFromJoinRequest(row: JoinRequestRow): Promise<void> {
  const email = row.email;
  setBusy(`join-invite-${email}`);
  setError(null);
  try {
    // Gmail first so Connect Gmail works when they land.
    if (row.wantsGmail) {
      try {
        const gRes = await api.addJoinRequestGoogleTestUser(email);
        const g = gRes.google;
        if (g?.consoleUrl && g.status !== "added") {
          await copyText(row.gmailEmail || row.email);
          window.open(g.consoleUrl, "_blank", "noopener,noreferrer");
        }
      } catch {
        // Non-fatal — invite still proceeds.
      }
    }

    const created = await api.createAdminInvite({ email, role: "user" });
    state.lastInviteLink = {
      email: created.email,
      inviteUrl: created.inviteUrl,
    };
    try {
      await api.patchJoinRequest(email, { status: "invited" });
    } catch {
      // Invite may still have succeeded — surface link either way.
    }
    const gmailNote = row.wantsGmail
      ? " Google Test users console opened if still needed — paste the Gmail address under Test users."
      : "";
    setNotice(
      `Invite sent to ${email}.${gmailNote} One-time link shown in Admin · Invites.`,
    );
    state.adminInvites = await api
      .listAdminInvites()
      .catch(() => state.adminInvites);
    const status =
      state.joinRequestStatusFilter === "all"
        ? undefined
        : state.joinRequestStatusFilter;
    state.joinRequests = await api.listJoinRequests(
      status ? { status } : undefined,
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

function filteredAdminInvites(): AdminInvite[] {
  const f = state.adminInviteStatusFilter;
  if (f === "all") return state.adminInvites;
  return state.adminInvites.filter((inv) => inv.status === f);
}

function adminInvitesPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-invites",
  });
  const productized = state.features["invites.productized"] === true;
  const isOwner = state.user?.role === "owner";

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Admin · Invites"),
        el(
          "p",
          { className: "sub" },
          "Invite-only signup. Create an invite to email a link + Cognito temp password. Copy invite URL if SES fails.",
        ),
      ),
    ),
  );

  if (!productized) {
    panel.append(
      alertBox(
        "warn",
        "Feature flag invites.productized is off — enable CONFIG#features in DynamoDB to use in-app invites.",
      ),
    );
    return panel;
  }

  if (state.lastInviteLink) {
    const linkBox = el("div", { className: "invite-url-box" });
    linkBox.append(
      el(
        "p",
        { className: "sub" },
        `One-time invite link for ${state.lastInviteLink.email} (shown once — copy now):`,
      ),
    );
    const urlRow = el("div", { className: "invite-url-row" });
    const urlInput = el("input", {
      type: "text",
      className: "input mono",
      readOnly: "true",
      value: state.lastInviteLink.inviteUrl,
    }) as HTMLInputElement;
    const copyBtn = el(
      "button",
      { type: "button", className: "btn-secondary btn-sm" },
      "Copy link",
    );
    copyBtn.addEventListener("click", () => {
      void (async () => {
        const ok = await copyText(state.lastInviteLink!.inviteUrl);
        setNotice(ok ? "Invite link copied." : "Could not copy — select and copy manually.");
        render();
      })();
    });
    const dismissBtn = el(
      "button",
      { type: "button", className: "btn-ghost btn-sm" },
      "Dismiss",
    );
    dismissBtn.addEventListener("click", () => {
      state.lastInviteLink = null;
      render();
    });
    urlRow.append(urlInput, copyBtn, dismissBtn);
    linkBox.append(urlRow);
    panel.append(linkBox);
  }

  // Create form
  const form = el("form", { className: "form admin-invite-form" });
  const email = el("input", {
    type: "email",
    name: "email",
    className: "input",
    placeholder: "tester@example.com",
    required: "true",
    autocomplete: "off",
  }) as HTMLInputElement;
  const role = el("select", {
    name: "role",
    className: "input",
  }) as HTMLSelectElement;
  role.append(
    el("option", { value: "user" }, "user"),
    ...(isOwner ? [el("option", { value: "admin" }, "admin")] : []),
  );
  form.append(
    el(
      "div",
      { className: "form-row" },
      el("label", null, "Email", email),
      el("label", null, "Role", role),
    ),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "invite-create" ? true : undefined,
        },
        state.busy === "invite-create" ? "Sending…" : "Send invite",
      ),
    ),
  );
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onCreateInvite(email.value.trim(), role.value as "user" | "admin");
  });
  panel.append(form);

  // Status filters
  const filters = el("div", { className: "filter-row" });
  for (const opt of [
    { id: "pending" as const, label: "Pending" },
    { id: "accepted" as const, label: "Accepted" },
    { id: "revoked" as const, label: "Revoked" },
    { id: "all" as const, label: "All" },
  ]) {
    const active = state.adminInviteStatusFilter === opt.id;
    const btn = el(
      "button",
      {
        type: "button",
        className: active ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === "invites-refresh" ? true : undefined,
      },
      opt.label,
    );
    btn.addEventListener("click", () => {
      if (state.adminInviteStatusFilter === opt.id) return;
      state.adminInviteStatusFilter = opt.id;
      render();
    });
    filters.append(btn);
  }
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "invites-refresh" ? true : undefined,
    },
    state.busy === "invites-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshAdminInvites();
  });
  filters.append(refreshBtn);
  panel.append(filters);

  const list = el("div", { className: "list" });
  const rows = filteredAdminInvites();
  if (rows.length === 0) {
    list.append(el("p", { className: "empty" }, "No invites match this filter."));
  } else {
    for (const inv of rows) {
      list.append(adminInviteRow(inv));
    }
  }
  panel.append(list);
  return panel;
}

function adminInviteRow(inv: AdminInvite): HTMLElement {
  const row = el("div", { className: "list-row admin-invite-row" });
  const pending = inv.status === "pending";
  const meta = el("div", { className: "list-main" });
  const statusBadgeClass =
    inv.status === "pending"
      ? "badge badge-warn"
      : inv.status === "accepted"
        ? "badge badge-ok"
        : inv.status === "revoked" || inv.status === "expired"
          ? "badge badge-err"
          : "badge badge-muted";
  meta.append(
    el(
      "div",
      { className: "list-title" },
      inv.email,
      " ",
      el("span", { className: statusBadgeClass }, inv.status),
      " ",
      el("span", { className: "badge badge-muted" }, inv.role),
    ),
    el(
      "div",
      { className: "list-meta" },
      `Created ${formatDate(inv.createdAt)} · expires ${formatDate(inv.expiresAt)}`,
      inv.resendCount > 0 ? ` · resent ×${inv.resendCount}` : "",
      inv.acceptedAt ? ` · accepted ${formatDate(inv.acceptedAt)}` : "",
    ),
  );
  row.append(meta);

  if (pending) {
    const actions = el("div", { className: "row-actions" });
    const resendBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `invite-resend-${inv.id}` ? true : undefined,
      },
      state.busy === `invite-resend-${inv.id}` ? "…" : "Resend",
    );
    resendBtn.addEventListener("click", () => {
      void onResendInvite(inv.id);
    });
    const revokeBtn = el(
      "button",
      {
        type: "button",
        className: "btn-danger btn-sm",
        disabled: state.busy === `invite-revoke-${inv.id}` ? true : undefined,
      },
      state.busy === `invite-revoke-${inv.id}` ? "…" : "Revoke",
    );
    revokeBtn.addEventListener("click", () => {
      if (
        !window.confirm(
          `Revoke invite for ${inv.email}? They will not be able to accept this link.`,
        )
      ) {
        return;
      }
      void onRevokeInvite(inv.id);
    });
    actions.append(resendBtn, revokeBtn);
    row.append(actions);
  }
  return row;
}

function adminUsersPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-users",
  });
  const isOwner = state.user?.role === "owner";

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Admin · Users"),
        el(
          "p",
          { className: "sub" },
          "Disable accounts, revoke all PATs, or (owner) change roles. Last owner cannot be demoted/disabled.",
        ),
      ),
      el(
        "button",
        {
          type: "button",
          className: "btn-ghost btn-sm",
          disabled: state.busy === "users-refresh" ? true : undefined,
        },
        state.busy === "users-refresh" ? "…" : "Refresh",
      ),
    ),
  );
  panel
    .querySelector(".panel-header button")
    ?.addEventListener("click", () => {
      void onRefreshAdminUsers();
    });

  const list = el("div", { className: "list" });
  if (state.adminUsers.length === 0) {
    list.append(el("p", { className: "empty" }, "No users yet."));
  } else {
    for (const u of state.adminUsers) {
      list.append(adminUserRow(u, isOwner));
    }
  }
  panel.append(list);
  return panel;
}

function adminUserRow(u: AdminUser, isOwner: boolean): HTMLElement {
  const row = el("div", { className: "list-row admin-user-row" });
  const self = state.user?.userId === u.userId;
  const active = u.status === "active";

  const meta = el("div", { className: "list-main" });
  meta.append(
    el(
      "div",
      { className: "list-title" },
      u.email,
      self ? el("span", { className: "badge" }, "you") : null,
      " ",
      el(
        "span",
        {
          className: active ? "badge badge-ok" : "badge badge-err",
        },
        u.status,
      ),
      " ",
      el("span", { className: "badge badge-muted" }, u.role),
    ),
    el(
      "div",
      { className: "list-meta" },
      `Joined ${formatDate(u.createdAt)} · ${u.userId.slice(0, 12)}…`,
    ),
  );
  row.append(meta);

  const actions = el("div", { className: "row-actions" });

  if (isOwner) {
    const roleSelect = el("select", {
      className: "input input-sm",
      disabled: state.busy === `user-role-${u.userId}` ? true : undefined,
    }) as HTMLSelectElement;
    for (const r of ["owner", "admin", "user"] as UserRole[]) {
      const opt = el("option", { value: r }, r) as HTMLOptionElement;
      if (r === u.role) opt.selected = true;
      roleSelect.append(opt);
    }
    roleSelect.addEventListener("change", () => {
      const next = roleSelect.value as UserRole;
      if (next === u.role) return;
      if (
        !window.confirm(`Change role for ${u.email} to ${next}?`)
      ) {
        roleSelect.value = u.role;
        return;
      }
      void onPatchUserRole(u.userId, next);
    });
    actions.append(roleSelect);
  }

  const patsBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === `user-pats-${u.userId}` ? true : undefined,
    },
    state.busy === `user-pats-${u.userId}` ? "…" : "Revoke PATs",
  );
  patsBtn.addEventListener("click", () => {
    if (!window.confirm(`Revoke all PATs for ${u.email}?`)) return;
    void onRevokeUserPats(u.userId);
  });
  actions.append(patsBtn);

  if (active) {
    const disBtn = el(
      "button",
      {
        type: "button",
        className: "btn-danger btn-sm",
        disabled:
          self || state.busy === `user-disable-${u.userId}` ? true : undefined,
      },
      state.busy === `user-disable-${u.userId}` ? "…" : "Disable",
    );
    disBtn.addEventListener("click", () => {
      if (
        !window.confirm(
          `Disable ${u.email}? They lose API/MCP access until re-enabled.`,
        )
      ) {
        return;
      }
      void onDisableUser(u.userId);
    });
    actions.append(disBtn);
  } else {
    const enBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `user-enable-${u.userId}` ? true : undefined,
      },
      state.busy === `user-enable-${u.userId}` ? "…" : "Enable",
    );
    enBtn.addEventListener("click", () => {
      void onEnableUser(u.userId);
    });
    actions.append(enBtn);
  }

  row.append(actions);
  return row;
}

async function onCreateInvite(
  email: string,
  role: "user" | "admin",
): Promise<void> {
  if (!email) {
    setError("Email is required");
    render();
    return;
  }
  setBusy("invite-create");
  setError(null);
  try {
    const created = await api.createAdminInvite({ email, role });
    state.lastInviteLink = {
      email: created.email,
      inviteUrl: created.inviteUrl,
    };
    const ses = created.emailDelivery?.ses ?? "unknown";
    setNotice(
      `Invite created for ${created.email} (SES: ${ses}). Copy the one-time link below if needed.`,
    );
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onResendInvite(inviteId: string): Promise<void> {
  setBusy(`invite-resend-${inviteId}`);
  setError(null);
  try {
    const created = await api.resendAdminInvite(inviteId);
    state.lastInviteLink = {
      email: created.email,
      inviteUrl: created.inviteUrl,
    };
    setNotice(`Invite resent to ${created.email}. New link shown once below.`);
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRevokeInvite(inviteId: string): Promise<void> {
  setBusy(`invite-revoke-${inviteId}`);
  setError(null);
  try {
    await api.revokeAdminInvite(inviteId);
    setNotice("Invite revoked.");
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRefreshAdminInvites(): Promise<void> {
  setBusy("invites-refresh");
  setError(null);
  try {
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRefreshAdminUsers(): Promise<void> {
  setBusy("users-refresh");
  setError(null);
  try {
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onDisableUser(userId: string): Promise<void> {
  setBusy(`user-disable-${userId}`);
  setError(null);
  try {
    await api.disableAdminUser(userId);
    setNotice("User disabled.");
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onEnableUser(userId: string): Promise<void> {
  setBusy(`user-enable-${userId}`);
  setError(null);
  try {
    await api.enableAdminUser(userId);
    setNotice("User enabled.");
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRevokeUserPats(userId: string): Promise<void> {
  setBusy(`user-pats-${userId}`);
  setError(null);
  try {
    const res = await api.revokeAdminUserPats(userId);
    setNotice(`PATs revoked (tokenVersion ${res.tokenVersion}).`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onPatchUserRole(userId: string, role: UserRole): Promise<void> {
  setBusy(`user-role-${userId}`);
  setError(null);
  try {
    await api.patchAdminUserRole(userId, role);
    setNotice(`Role updated to ${role}.`);
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

function adminFlagsPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  const anomalyOn = state.features["abuse.anomalyFlags"] === true;

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Anomaly flags"),
        el(
          "p",
          { className: "sub" },
          "Admin-only abuse signals (rate denials when abuse.anomalyFlags is on). Ack after review.",
        ),
      ),
    ),
  );

  if (!anomalyOn) {
    panel.append(
      alertBox(
        "info",
        "Feature flag abuse.anomalyFlags is off — new flags are not recorded. Existing open flags still list here.",
      ),
    );
  }

  // Status filter chips
  const filters = el("div", { className: "filter-row" });
  for (const opt of [
    { id: "open" as const, label: "Open" },
    { id: "acked" as const, label: "Acked" },
    { id: "all" as const, label: "All" },
  ]) {
    const active = state.adminFlagsStatusFilter === opt.id;
    const btn = el(
      "button",
      {
        type: "button",
        className: active ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === "flags-refresh" ? true : undefined,
      },
      opt.label,
    );
    btn.addEventListener("click", () => {
      if (state.adminFlagsStatusFilter === opt.id) return;
      state.adminFlagsStatusFilter = opt.id;
      void onRefreshFlags();
    });
    filters.append(btn);
  }
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "flags-refresh" ? true : undefined,
    },
    state.busy === "flags-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshFlags();
  });
  filters.append(refreshBtn);
  panel.append(filters);

  const list = el("div", { className: "list" });
  if (state.adminFlags.length === 0) {
    list.append(el("p", { className: "empty" }, "No flags match this filter."));
  } else {
    for (const f of state.adminFlags) {
      list.append(adminFlagRow(f));
    }
  }
  panel.append(list);

  if (state.adminFlagsNextCursor) {
    const more = el(
      "div",
      { className: "form-actions", style: "margin-top:0.75rem" },
      el(
        "button",
        {
          type: "button",
          className: "btn-secondary btn-sm",
          disabled: state.busy === "flags-more" ? true : undefined,
        },
        state.busy === "flags-more" ? "Loading…" : "Load more",
      ),
    );
    more.querySelector("button")?.addEventListener("click", () => {
      void onLoadMoreFlags();
    });
    panel.append(more);
  }

  return panel;
}

// ── Actions ───────────────────────────────────────────────────────────────

async function handleLoginResult(
  res: Awaited<ReturnType<typeof api.login>>,
  email: string,
): Promise<void> {
  if (isLoginChallenge(res)) {
    const name = res.challenge.name;
    if (name === "NEW_PASSWORD_REQUIRED") {
      state.challenge = {
        kind: "NEW_PASSWORD_REQUIRED",
        session: res.challenge.session,
        email,
      };
      setError(null);
      return;
    }
    if (name === "SOFTWARE_TOKEN_MFA") {
      state.challenge = {
        kind: "SOFTWARE_TOKEN_MFA",
        session: res.challenge.session,
        email,
      };
      setError(null);
      return;
    }
    setError(`Unsupported challenge: ${name}`);
    return;
  }

  state.challenge = null;
  state.user = res.user;
  setError(null);
  setNotice(null);
  try {
    // Bind invite after session is established (JWT sub + email match).
    if (state.invite.token || readSessionInviteToken()) {
      await tryAcceptPendingInvite();
    }
    await refreshDashboard();
  } catch (err) {
    setError(errMessage(err));
  }
}

async function onForgotPasswordRequest(email: string): Promise<void> {
  if (!email) {
    setError("Email is required");
    render();
    return;
  }
  setBusy("forgot");
  setError(null);
  try {
    const res = await api.forgotPassword(email);
    state.forgotPassword = { step: "confirm", email };
    setNotice(
      res.message ??
        "If an account exists for that email, we sent a verification code.",
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onForgotPasswordConfirm(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  if (!email || !code || !newPassword) {
    setError("Email, code, and new password are required");
    render();
    return;
  }
  setBusy("forgot");
  setError(null);
  try {
    const res = await api.confirmForgotPassword({ email, code, newPassword });
    state.forgotPassword = null;
    setNotice(
      res.message ?? "Password updated. Sign in with your new password.",
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onLogin(email: string, password: string): Promise<void> {
  if (!email || !password) {
    setError("Email and password are required");
    render();
    return;
  }
  setBusy("login");
  setError(null);
  try {
    const res = await api.login(email, password);
    await handleLoginResult(res, email);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onNewPassword(
  email: string,
  session: string,
  newPassword: string,
): Promise<void> {
  setBusy("login");
  setError(null);
  try {
    const res = await api.respondNewPassword({ session, email, newPassword });
    await handleLoginResult(res, email);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onTotp(
  email: string,
  session: string,
  totpCode: string,
): Promise<void> {
  setBusy("login");
  setError(null);
  try {
    const res = await api.respondTotp({ session, email, totpCode });
    await handleLoginResult(res, email);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onLogout(): Promise<void> {
  setBusy("logout");
  state.profileMenuOpen = false;
  try {
    await api.logout();
  } catch {
    // Logout is idempotent; clear local state anyway
  } finally {
    state.user = null;
    state.accounts = [];
    state.selectedAccountId = null;
    state.tokens = [];
    state.mcpClients = [];
    state.minted = null;
    state.features = { ...DEFAULT_FEATURE_FLAGS };
    state.addAccountOpen = false;
    state.auditEvents = [];
    state.auditNextCursor = null;
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    state.adminFlagsStatusFilter = "open";
    state.challenge = null;
    state.billing = null;
    state.usage = null;
    setBusy(null);
    setError(null);
    setNotice(null);
    try {
      window.history.replaceState({}, "", "/");
    } catch {
      // ignore
    }
    render();
  }
}

async function onConnectGmail(): Promise<void> {
  setError("Gmail OAuth is not available on mcpto.ai — connect an AI provider key instead.");
  render();
}

async function onUpdateAccount(
  id: string,
  body: api.UpdateAccountBody,
): Promise<boolean> {
  setBusy(`edit-${id}`);
  setError(null);
  setNotice(null);
  try {
    const updated = await api.updateAccount(id, body);
    state.accounts = state.accounts.map((a) =>
      a.id === id ? { ...a, ...updated } : a,
    );
    setNotice(`Updated ${updated.label}`);
    return true;
  } catch (err) {
    setError(errMessage(err));
    return false;
  } finally {
    setBusy(null);
  }
}

async function onRotateKey(id: string, apiKey: string): Promise<boolean> {
  setBusy(`edit-${id}`);
  setError(null);
  setNotice(null);
  try {
    const updated = await api.rotateAccountKey(id, apiKey);
    state.accounts = state.accounts.map((a) =>
      a.id === id ? { ...a, ...updated } : a,
    );
    setNotice(`Key updated for ${updated.label}`);
    return true;
  } catch (err) {
    setError(errMessage(err));
    return false;
  } finally {
    setBusy(null);
  }
}

async function onVerifyAccount(id: string): Promise<void> {
  setBusy(`verify-${id}`);
  setError(null);
  setNotice(null);
  try {
    const updated = await api.verifyAccount(id);
    state.accounts = state.accounts.map((a) =>
      a.id === id ? { ...a, ...updated } : a,
    );
    setNotice(
      updated.status === "ok"
        ? `Verified ${updated.label}`
        : `Verify finished: ${updated.status}${updated.lastError ? " — " + updated.lastError : ""}`,
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onAddAiAccount(
  body: api.CreateAiAccountBody,
  clearForm: () => void,
): Promise<void> {
  if (!body.label.trim() || !body.apiKey.trim()) {
    setError("Label and API key are required");
    render();
    return;
  }
  setBusy("add-ai");
  setError(null);
  setNotice(null);
  try {
    const created = await api.createAiAccount(body);
    state.accounts = [created, ...state.accounts.filter((a) => a.id !== created.id)];
    clearForm();
    state.addAccountOpen = false;
    state.selectedAccountId = created.id;
    setNotice(`Connected ${created.label} (${created.provider})`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onDeleteAccount(id: string): Promise<void> {
  setBusy(`del-acc-${id}`);
  setError(null);
  try {
    await api.deleteAccount(id);
    state.accounts = state.accounts.filter((a) => a.id !== id);
    if (state.selectedAccountId === id) state.selectedAccountId = null;
    setNotice("Account disconnected");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onMint(
  name: string,
  expiresInDays: number,
  scopes: PatScope[],
  clearForm: () => void,
): Promise<void> {
  if (!name) {
    setError("Token name is required");
    render();
    return;
  }
  if (scopes.length === 0) {
    setError("Select at least one scope (read, send, or full)");
    render();
    return;
  }
  setBusy("mint");
  setError(null);
  setNotice(null);
  try {
    const minted = await api.mintToken({ name, expiresInDays, scopes });
    // Prefer server scopes; fall back to what we requested if older server omits them.
    const effectiveScopes =
      Array.isArray(minted.scopes) && minted.scopes.length > 0
        ? scopesOf(minted)
        : scopes;
    state.minted = { ...minted, scopes: effectiveScopes };
    // Add metadata to list (without raw token)
    const meta: PatMeta = {
      id: minted.id,
      name: minted.name,
      tokenPrefix: minted.tokenPrefix,
      createdAt: minted.createdAt,
      expiresAt: minted.expiresAt,
      scopes: effectiveScopes,
    };
    state.tokens = [meta, ...state.tokens.filter((t) => t.id !== meta.id)];
    // Minted PATs show in Active clients via merge (pat_{id}) even before first /mcp use.
    const patClientId = `pat_${minted.id}`;
    const patClient: McpClient = {
      id: patClientId,
      kind: "pat",
      name: minted.name,
      status: "active",
      createdAt: minted.createdAt,
      lastUsedAt: minted.createdAt,
      scopes: effectiveScopes,
      patTokenId: minted.id,
    };
    state.mcpClients = [
      patClient,
      ...state.mcpClients.filter((c) => c.id !== patClientId),
    ];
    clearForm();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onDisconnectMcpClient(clientId: string): Promise<void> {
  setBusy(`disc-${clientId}`);
  setError(null);
  try {
    await api.disconnectMcpClient(clientId);
    const wasPat = clientId.startsWith("pat_");
    const patTokenId = wasPat ? clientId.slice("pat_".length) : undefined;
    state.mcpClients = state.mcpClients.map((c) =>
      c.id === clientId ? { ...c, status: "revoked" as const } : c,
    );
    // Drop revoked PATs from the tokens list and minted secret if present.
    if (patTokenId) {
      state.tokens = state.tokens.filter((t) => t.id !== patTokenId);
      if (state.minted?.id === patTokenId) state.minted = null;
      // Remove PAT row entirely (revoked tokens are deleted server-side).
      state.mcpClients = state.mcpClients.filter((c) => c.id !== clientId);
    }
    setNotice("Client disconnected");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onRefreshAudit(): Promise<void> {
  setBusy("audit-refresh");
  setError(null);
  try {
    const page = await api.listAudit({ limit: AUDIT_PAGE_SIZE });
    state.auditEvents = page.events;
    state.auditNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onLoadMoreAudit(): Promise<void> {
  if (!state.auditNextCursor) return;
  setBusy("audit-more");
  setError(null);
  try {
    const page = await api.listAudit({
      limit: AUDIT_PAGE_SIZE,
      cursor: state.auditNextCursor,
    });
    const seen = new Set(state.auditEvents.map((e) => e.id));
    for (const ev of page.events) {
      if (!seen.has(ev.id)) {
        state.auditEvents.push(ev);
        seen.add(ev.id);
      }
    }
    state.auditNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onRefreshFlags(): Promise<void> {
  if (!state.user || !isAdminRole(state.user.role)) return;
  setBusy("flags-refresh");
  setError(null);
  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const page = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      ...(status ? { status } : {}),
    });
    state.adminFlags = page.flags;
    state.adminFlagsNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onLoadMoreFlags(): Promise<void> {
  if (!state.adminFlagsNextCursor) return;
  if (!state.user || !isAdminRole(state.user.role)) return;
  setBusy("flags-more");
  setError(null);
  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const page = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      cursor: state.adminFlagsNextCursor,
      ...(status ? { status } : {}),
    });
    const seen = new Set(state.adminFlags.map((f) => f.flagId));
    for (const f of page.flags) {
      if (!seen.has(f.flagId)) {
        state.adminFlags.push(f);
        seen.add(f.flagId);
      }
    }
    state.adminFlagsNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onAckFlag(flagId: string): Promise<void> {
  setBusy(`ack-flag-${flagId}`);
  setError(null);
  try {
    const updated = await api.ackAdminFlag(flagId);
    state.adminFlags = state.adminFlags.map((f) =>
      f.flagId === flagId ? updated : f,
    );
    // If filtering open only, drop acked from list.
    if (state.adminFlagsStatusFilter === "open") {
      state.adminFlags = state.adminFlags.filter(
        (f) => String(f.status).toLowerCase() === "open",
      );
    }
    setNotice(`Flag ${flagId} acknowledged`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}
